import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  LinearProgress,
  Tooltip,
  Drawer,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  CheckCircle,
  Schedule,
  Lock,
  LockOpen,
  UnfoldMore,
  UnfoldLess,
  RadioButtonUnchecked,
  ArrowUpward,
  ArrowDownward,
  ArrowForward,
  Description,
  Close,
  ContentCopy,
  PushPin,
  Download,
  Upload,
  Cloud,
  Storage,
  Assessment,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  ExecutionPlan, 
  ExecutionPhase, 
  ExecutionTask, 
  ExecutionAction,
  KPIScore,
  ProjectPlan,
  ExecutionStatus,
  ChangeOrderListItem,
} from '../types';
import { mockProjectPlan } from '../data/mockProjectPlan';
import { getProject, unlockProject, downloadChangeOrderExport, validateChangeOrder, commitChangeOrder, getProjectChangeOrders, ProjectApiError, type ChangeOrderValidateResponse } from '../services/projectApi';
import { hasRole, isFraportUser, isTenantOrClientUser } from '../services/authApi';
import {
  getExecutionPlan,
  createExecutionPlan,
  getTaskAssignments,
  ExecutionPlanResponse,
  PlanningExecutionApiError,
  getOrganizationStats,
  OrganizationStat,
  getActionCompletionStatus,
  CompletionStatus,
  getActionAssignments,
} from '../services/planningExecutionApi';
import { OrganizationStats } from '../components/OrganizationStats/OrganizationStats';
import { ProjectDateControls } from '../components/ProjectExecution/ProjectDateControls';
import SharePointDocumentViewer from '../components/ProjectExecution/SharePointDocumentViewer';
import { AuditLogView } from '../components/ProjectExecution/AuditLogView';
import { ActionComments } from '../components/ProjectExecution/ActionComments';
import LibraryBrowser from '../components/LibraryBrowser/LibraryBrowser';
import LargeFilesBrowser from '../components/Documents/LargeFilesBrowser';
import { apiService } from '../services/api';
import { getUserAccessToken } from '../utils/tokenStorage';
import { DocumentAssociation, FileMetadata } from '../types';
import { ProjectSelector } from '../components/ProjectSelector/ProjectSelector';
import SaveTemplateDialog from '../components/Template/SaveTemplateDialog';
import ProjectUserManagement from '../components/Project/ProjectUserManagement';
import GanttChartView, { executionPlanToGanttData } from '../components/GanttChart/GanttChartView';
import { useAuth } from '../contexts/AuthContext';
import { shouldOpenSharePointDirectly } from '../utils/sharePointLinkUtils';

// Helper function to parse date string in local time (avoid timezone shifts)
// Backend now returns dates as YYYY-MM-DD strings (no timezone conversion)
// Simply extract the date part and return it as-is - no parsing needed
const parseDateStringLocal = (dateStr: string | null | undefined): string | null => {
  if (!dateStr) return null;
  // Extract date part (YYYY-MM-DD) and return as-is
  // Backend fix ensures dates are returned correctly, no timezone conversion needed
  const dateOnly = dateStr.split('T')[0];
  // Validate it's in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    return dateOnly; // Return as-is - backend already handles timezone correctly
  }
  return null; // Invalid format
};

// Helper function to convert Date object to local date string (YYYY-MM-DD)
const dateToLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to convert date string (YYYY-MM-DD) to Date object in local time for formatting
// This ensures dates are parsed in local time, not UTC, to avoid timezone shifts
const dateStringToLocalDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null;
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

/** Resolve source action to Phase.Task.Action (e.g. "2.5.1"). Project id is omitted in the UI. */
const getSourceActionPosition = (executionPlan: ExecutionPlan, sourceActionId: number): string | null => {
  const targetId = Number(sourceActionId);
  if (!Number.isFinite(targetId)) return null;
  for (let pi = 0; pi < executionPlan.phases.length; pi++) {
    const phase = executionPlan.phases[pi];
    const phaseOrder = phase.displayOrder ?? pi + 1;
    for (let ti = 0; ti < phase.tasks.length; ti++) {
      const task = phase.tasks[ti];
      const taskOrder = task.displayOrder ?? ti + 1;
      for (let ai = 0; ai < task.actions.length; ai++) {
        const act = task.actions[ai];
        if (Number(act.actionId) === targetId) {
          const actionOrder = act.displayOrder ?? ai + 1;
          return [phaseOrder, taskOrder, actionOrder].join('.');
        }
      }
    }
  }
  return null;
};

const ItemDescription = ({ text }: { text?: string | null }) => {
  if (!text?.trim()) return null;
  return (
    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.4, mt: 0.25 }}>
      {text}
    </Typography>
  );
};

// Helper function to convert PlanningPlan to ExecutionPlan
const convertPlanToExecution = (plan: ProjectPlan): ExecutionPlan => {
  const executionPlan: ExecutionPlan = {
    projectId: plan.projectId,
    projectName: plan.projectName,
    projectStartDate: plan.projectStartDate,
    phases: plan.phases.map(phase => ({
      phaseId: phase.phaseId,
      name: phase.name,
      description: phase.description,
      contractedStartDate: phase.startDate,
      contractedEndDate: phase.endDate,
      contractedDuration: phase.duration,
      actualStartDate: null,
      actualEndDate: null,
      actualDuration: 0,
      tasks: phase.tasks.map(task => ({
        taskId: task.taskId,
        name: task.name,
        description: task.description,
        assignedUsers: task.assignedUsers,
        contractedStartDate: task.startDate,
        contractedEndDate: task.endDate,
        contractedDuration: task.duration,
        actualStartDate: null,
        actualEndDate: null,
        actualDuration: 0,
        prerequisites: task.prerequisites,
        parallelExecution: task.parallelExecution,
        actions: task.actions.map(action => ({
          actionId: action.actionId,
          actionType: action.actionType,
          duration: action.duration,
          assignedTo: action.assignedTo,
          notes: action.notes,
          displayOrder: action.displayOrder,
          contractedStartDate: null, // Will be calculated
          contractedEndDate: null, // Will be calculated
          actualStartDate: null,
          actualEndDate: null,
          status: 'NOT_STARTED' as const,
          kpiScore: 0,
          isLocked: false,
        })),
        displayOrder: task.displayOrder,
        status: 'NOT_STARTED' as const,
        kpiScore: 0,
      })),
      displayOrder: phase.displayOrder,
      status: 'NOT_STARTED' as const,
      kpiScore: 0,
    })),
    changeOrders: [],
    auditLog: [],
  };

  // Calculate contracted dates for actions based on task start dates
  executionPlan.phases.forEach(phase => {
    phase.tasks.forEach(task => {
      if (task.contractedStartDate) {
        let currentActionStart = new Date(task.contractedStartDate);
        task.actions.forEach(action => {
          // Calculate action contracted dates sequentially
          action.contractedStartDate = dateToLocalDateString(currentActionStart);
          // Simple calculation: assume 8-hour workdays
          const days = action.duration / 8;
          const endDate = new Date(currentActionStart);
          endDate.setDate(endDate.getDate() + Math.ceil(days));
          action.contractedEndDate = dateToLocalDateString(endDate);
          currentActionStart = new Date(endDate);
        });
      }
    });
  });

  return executionPlan;
};

// Helper function to calculate KPI score
// NOTE: KPI calculation is now handled by the backend automatically when action status changes to COMPLETE.
// The backend calculates KPI using duration-based calculation (business days) instead of date-based (calendar days).
// Frontend should rely on kpiScore values returned from the API rather than calculating locally.
// This function is kept for backward compatibility but should not be used for KPI calculation.
// @ts-ignore - intentionally unused
const _calculateKPI = (contractedDate: string | null, actualDate: string | null): KPIScore => {
  if (!contractedDate || !actualDate) return 0;
  const diffMs = new Date(actualDate).getTime() - new Date(contractedDate).getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Helper function to calculate percent complete for an action
const calculateActionPercentComplete = (action: ExecutionAction): number => {
  if (action.status === 'COMPLETE' || action.status === 'LOCKED') {
    return 100;
  } else if (action.status === 'IN_PROGRESS') {
    // Only calculate progress if we have an actual end date (action is complete)
    // If no actual end date, return 0% - we can't calculate progress until the action is finished
    if (action.actualEndDate) {
      // Action has been completed, calculate based on actual vs contracted dates
      if (action.contractedStartDate && action.contractedEndDate && action.actualStartDate) {
        const totalDuration = dateStringToLocalDate(action.contractedEndDate)!.getTime() - dateStringToLocalDate(action.contractedStartDate)!.getTime();
        const actualDuration = dateStringToLocalDate(action.actualEndDate)!.getTime() - dateStringToLocalDate(action.actualStartDate)!.getTime();
        if (totalDuration > 0) {
          const percent = Math.min(100, Math.max(0, (actualDuration / totalDuration) * 100));
          return Math.round(percent);
        }
      }
      // If we have actualEndDate but can't calculate, assume 100% (it's complete)
      return 100;
    }
    // If in progress but no actual end date, return 0% (can't calculate progress until finished)
    return 0;
  }
  return 0; // NOT_STARTED
};

// Helper function to calculate percent complete for a task (weighted by action duration)
const calculateTaskPercentComplete = (task: ExecutionTask): number => {
  if (task.actions.length === 0) {
    // If no actions, base on status only
    return task.status === 'COMPLETE' || task.status === 'LOCKED' ? 100 : 0;
  }

  // Calculate weighted average based on action durations
  let totalWeight = 0;
  let completedWeight = 0;

  task.actions.forEach(action => {
    // Ensure duration is a valid number (default to 1 if undefined/NaN)
    const weight = (typeof action.duration === 'number' && !isNaN(action.duration) && action.duration > 0) 
      ? action.duration 
      : 1;
    totalWeight += weight;
    const actionPercent = calculateActionPercentComplete(action);
    completedWeight += weight * (actionPercent / 100);
  });

  if (totalWeight === 0) return 0;
  const result = Math.round((completedWeight / totalWeight) * 100);
  // Ensure result is a valid number
  return isNaN(result) ? 0 : result;
};

// Helper function to calculate percent complete for a phase (weighted by task duration)
const calculatePhasePercentComplete = (phase: ExecutionPhase): number => {
  if (phase.tasks.length === 0) {
    // If no tasks, base on status only
    return phase.status === 'COMPLETE' || phase.status === 'LOCKED' ? 100 : 0;
  }

  // Calculate weighted average based on task durations
  let totalWeight = 0;
  let completedWeight = 0;

  phase.tasks.forEach(task => {
    // Ensure contractedDuration is a valid number (default to 1 if undefined/NaN)
    const weight = (typeof task.contractedDuration === 'number' && !isNaN(task.contractedDuration) && task.contractedDuration > 0)
      ? task.contractedDuration
      : 1;
    totalWeight += weight;
    const taskPercent = calculateTaskPercentComplete(task);
    completedWeight += weight * (taskPercent / 100);
  });

  if (totalWeight === 0) return 0;
  const result = Math.round((completedWeight / totalWeight) * 100);
  // Ensure result is a valid number
  return isNaN(result) ? 0 : result;
};

// Helper function to calculate percent complete for a project (weighted by phase duration)
const calculateProjectPercentComplete = (executionPlan: ExecutionPlan): number => {
  if (executionPlan.phases.length === 0) {
    return 0;
  }

  // Calculate weighted average based on phase durations
  let totalWeight = 0;
  let completedWeight = 0;

  executionPlan.phases.forEach(phase => {
    // Ensure contractedDuration is a valid number (default to 1 if undefined/NaN)
    const weight = (typeof phase.contractedDuration === 'number' && !isNaN(phase.contractedDuration) && phase.contractedDuration > 0)
      ? phase.contractedDuration
      : 1;
    totalWeight += weight;
    const phasePercent = calculatePhasePercentComplete(phase);
    completedWeight += weight * (phasePercent / 100);
  });

  if (totalWeight === 0) return 0;
  const result = Math.round((completedWeight / totalWeight) * 100);
  return isNaN(result) ? 0 : result;
};

// Helper function to calculate project-level KPI (worst case from phases)
const calculateProjectKPI = (executionPlan: ExecutionPlan): KPIScore => {
  if (executionPlan.phases.length === 0) return 0;
  
  const phaseKPIs = executionPlan.phases.map(phase => phase.kpiScore);
  // Use worst case (most late) KPI
  return Math.max(...phaseKPIs);
};

// Helper function to determine project status from phases
const calculateProjectStatus = (executionPlan: ExecutionPlan): ExecutionStatus => {
  if (executionPlan.phases.length === 0) return 'NOT_STARTED';
  
  const allComplete = executionPlan.phases.every(p => p.status === 'COMPLETE' || p.status === 'LOCKED');
  const anyInProgress = executionPlan.phases.some(p => p.status === 'IN_PROGRESS');
  const anyComplete = executionPlan.phases.some(p => p.status === 'COMPLETE' || p.status === 'LOCKED');
  
  if (allComplete) return 'COMPLETE';
  if (anyInProgress || anyComplete) return 'IN_PROGRESS';
  return 'NOT_STARTED';
};

// Helper function to get project contracted dates
const getProjectContractedDates = (executionPlan: ExecutionPlan): { start: string | null; end: string | null } => {
  // Collect all contracted start and end dates from phases, tasks, and actions
  const startDates: string[] = [];
  const endDates: string[] = [];
  
  executionPlan.phases.forEach(phase => {
    // Add phase dates
    if (phase.contractedStartDate) startDates.push(phase.contractedStartDate);
    if (phase.contractedEndDate) endDates.push(phase.contractedEndDate);
    
    // Add task dates
    phase.tasks.forEach(task => {
      if (task.contractedStartDate) startDates.push(task.contractedStartDate);
      if (task.contractedEndDate) endDates.push(task.contractedEndDate);
      
      // Add action dates
      task.actions.forEach(action => {
        if (action.contractedStartDate) startDates.push(action.contractedStartDate);
        if (action.contractedEndDate) endDates.push(action.contractedEndDate);
      });
    });
  });
  
  // Find earliest start date and latest end date
  let earliestStart: string | null = null;
  let latestEnd: string | null = null;
  
  if (startDates.length > 0) {
    const startTimes = startDates.map(d => {
      const date = dateStringToLocalDate(d);
      return date ? date.getTime() : 0;
    }).filter(t => t > 0);
    
    if (startTimes.length > 0) {
      const minTime = Math.min(...startTimes);
      earliestStart = dateToLocalDateString(new Date(minTime));
    }
  }
  
  if (endDates.length > 0) {
    const endTimes = endDates.map(d => {
      const date = dateStringToLocalDate(d);
      return date ? date.getTime() : 0;
    }).filter(t => t > 0);
    
    if (endTimes.length > 0) {
      const maxTime = Math.max(...endTimes);
      latestEnd = dateToLocalDateString(new Date(maxTime));
    }
  }
  
  // Fallback to projectStartDate if no contracted dates found
  if (!earliestStart && executionPlan.projectStartDate) {
    earliestStart = executionPlan.projectStartDate;
  }
  
  return {
    start: earliestStart,
    end: latestEnd,
  };
};

// Helper function to get project actual dates from API data only (no frontend execution date calculation).
// EPC-F1/EPC-F2: All execution dates must come from backend; display only refetched execution plan data.
const getProjectActualDates = (executionPlan: ExecutionPlan): { start: string | null; end: string | null } => {
  if (executionPlan.phases.length === 0) return { start: null, end: null };
  
  const startDates = executionPlan.phases
    .map(p => p.actualStartDate)
    .filter((d): d is string => d !== null);
  const endDates = executionPlan.phases
    .map(p => p.actualEndDate)
    .filter((d): d is string => d !== null);
  
  const start = startDates.length > 0 ? new Date(Math.min(...startDates.map(d => new Date(d).getTime()))).toISOString().split('T')[0] : null;
  const end = endDates.length > 0 ? new Date(Math.max(...endDates.map(d => new Date(d).getTime()))).toISOString().split('T')[0] : null;
  return { start, end };
};

// Helper function to get KPI color
// Negative KPI = reclaimed time (ahead of schedule) = GREEN (good)
// Positive KPI = overdue/delayed = RED (bad)
// Zero = on time = default (gray)
const getKPIColor = (kpi: KPIScore): 'success' | 'warning' | 'error' | 'default' => {
  if (kpi < 0) return 'success'; // Reclaimed time (negative = ahead of schedule) = GREEN
  if (kpi === 0) return 'default'; // On time
  return 'error'; // Overdue (positive = delayed) = RED
};

// Helper function to get status color
const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'COMPLETE': return 'success';
    case 'IN_PROGRESS': return 'warning';
    case 'LOCKED': return 'default';
    default: return 'default';
  }
};

const ProjectExecutionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPublicExecutionUser = isTenantOrClientUser(user);
  const [searchParams] = useSearchParams();
  const urlProjectId = searchParams.get('projectId');
  const [executionPlan, setExecutionPlan] = useState<ExecutionPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [projectStatus, setProjectStatus] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [_changeOrderDialog, setChangeOrderDialog] = useState({ open: false, item: null as any });
  const [_auditLogDialog, _setAuditLogDialog] = useState({ open: false });
  const [organizationStats, setOrganizationStats] = useState<OrganizationStat[]>([]);
  const [organizationStatsLoading, setOrganizationStatsLoading] = useState(false);
  const [projectDate, setProjectDate] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFolderPath, _setViewerFolderPath] = useState<string | undefined>();
  const [projectLibraryName, setProjectLibraryName] = useState<string | null>(null);
  const [projectSiteId, setProjectSiteId] = useState<string | null>(null);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [unlockConfirmOpen, setUnlockConfirmOpen] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [documentsDrawerOpen, setDocumentsDrawerOpen] = useState(false);
  const [documentsDrawerPinned, setDocumentsDrawerPinned] = useState(false);
  const [documentsDrawerTab, setDocumentsDrawerTab] = useState(0);
  const [documentsDrawerError, setDocumentsDrawerError] = useState<string | null>(null);
  const [changeOrdersList, setChangeOrdersList] = useState<ChangeOrderListItem[]>([]);
  // TODO: Get this from configuration or API - for now using hardcoded default site
  const DEFAULT_SITE_ID = '7de7c338-76a1-472e-8f6a-f2b95c3bb7d5';

  // Helper function to convert API ExecutionPlanResponse to ExecutionPlan
  const convertApiResponseToExecutionPlan = (apiResponse: ExecutionPlanResponse): ExecutionPlan => {
    const phases: ExecutionPhase[] = apiResponse.phases.map(phase => {
      const tasks: ExecutionTask[] = phase.tasks.map(task => {
        const actions: ExecutionAction[] = task.actions.map(action => {
          return {
            actionId: action.actionId,
            actionType: action.actionType,
            name: action.name ?? null,
            description: action.description ?? null,
            // Backend returns both duration and durationHours (alias) - use duration first, fallback to durationHours
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            duration: action.duration || (action as any).durationHours || 0,
            assignedTo: [], // Will load separately if needed
            notes: action.notes || '',
            displayOrder: action.displayOrder,
            // Use contractedStartDate if available, fallback to plannedStartDate
            // Backend returns contractedStartDate for actions when project is locked
            // Parse dates in local time to avoid timezone shifts
            contractedStartDate: action.contractedStartDate 
              ? parseDateStringLocal(action.contractedStartDate)
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              : ((action as any).plannedStartDate 
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ? parseDateStringLocal((action as any).plannedStartDate)
                  : null),
            // Use contractedEndDate if available, fallback to plannedEndDate
            // Backend returns contractedEndDate for actions when project is locked
            contractedEndDate: action.contractedEndDate 
              ? parseDateStringLocal(action.contractedEndDate)
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              : ((action as any).plannedEndDate 
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ? parseDateStringLocal((action as any).plannedEndDate)
                  : null),
            actualStartDate: parseDateStringLocal(action.actualStartDate),
            actualEndDate: parseDateStringLocal(action.actualEndDate),
            status: action.status as ExecutionStatus,
            kpiScore: action.kpiScore || 0,
            isLocked: action.status === 'LOCKED' || action.status === 'COMPLETE',
            documentName: (action as { documentName?: string | null }).documentName ?? null,
            instructions: (action as { instructions?: string | null }).instructions ?? null,
            sourceActionId: (action as { sourceActionId?: number | null }).sourceActionId ?? null,
            rejectActionId: (action as { rejectActionId?: number | null }).rejectActionId ?? null,
            prerequisites: (() => {
              const rawPrereqs = (action as { prerequisites?: Array<{ actionId?: number; taskId?: number; phaseId?: number } | number> }).prerequisites;
              if (!Array.isArray(rawPrereqs)) return [];
              return rawPrereqs
                .map((p) => {
                  if (typeof p === 'number') return p;
                  if (p && typeof p === 'object') return p.actionId ?? p.taskId ?? p.phaseId;
                  return parseInt(String(p), 10);
                })
                .filter((id): id is number => id != null && !isNaN(id) && id > 0);
            })(),
            sourceDocuments: (action as { sourceDocuments?: ExecutionAction['sourceDocuments'] }).sourceDocuments ?? [],
          };
        });

        // Handle prerequisites - API returns 'prerequisites' field (or 'prerequisiteTaskIds' as fallback)
        // Backend returns prerequisites as number[] or empty array []
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const prerequisites = (task as any).prerequisites || (task as any).prerequisiteTaskIds || [];
        // Ensure prerequisites is always an array of numbers
        const prerequisitesArray = Array.isArray(prerequisites)
          ? prerequisites.map(id => typeof id === 'number' ? id : parseInt(String(id), 10)).filter(id => !isNaN(id))
          : [];
        

        return {
          taskId: task.taskId,
          name: task.name,
          description: task.description || '',
          assignedUsers: [], // Will load separately if needed
          // Use contractedStartDate if available, fallback to plannedStartDate
          // Backend returns contractedStartDate for tasks
          // Parse dates in local time to avoid timezone shifts
          contractedStartDate: (() => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const dateStr = task.contractedStartDate || (task as any).plannedStartDate;
            if (!dateStr) return null;
            return parseDateStringLocal(dateStr);
          })(),
          // Use contractedEndDate if available, fallback to plannedEndDate
          // Backend returns contractedEndDate for tasks
          contractedEndDate: (() => {
            const dateStr = task.contractedEndDate || // eslint-disable-next-line @typescript-eslint/no-explicit-any
(task as any).plannedEndDate;
            if (!dateStr) return null;
            return parseDateStringLocal(dateStr);
          })(),
          contractedDuration: task.contractedDurationHours || // eslint-disable-next-line @typescript-eslint/no-explicit-any
(task as any).plannedDurationHours || 0,
          actualStartDate: parseDateStringLocal(task.actualStartDate),
          actualEndDate: parseDateStringLocal(task.actualEndDate),
          actualDuration: task.actualDurationHours || 0,
          prerequisites: prerequisitesArray,
          parallelExecution: task.parallelExecution,
          actions,
          displayOrder: task.displayOrder,
          status: task.status as ExecutionStatus,
          kpiScore: task.kpiScore || 0,
        };
      });

        const phaseWithPrereqs = phase as unknown as { prerequisites?: number[] };
        const phasePrereqs = Array.isArray(phaseWithPrereqs.prerequisites)
          ? phaseWithPrereqs.prerequisites
          : [];
      return {
        phaseId: phase.phaseId,
        name: phase.name,
        description: phase.description || '',
        prerequisites: phasePrereqs,
        // Use contractedStartDate if available, fallback to plannedStartDate
        // Backend returns contractedStartDate for phases
        // Parse dates in local time to avoid timezone shifts
        contractedStartDate: (() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const dateStr = phase.contractedStartDate || (phase as any).plannedStartDate;
          if (!dateStr) return null;
          return parseDateStringLocal(dateStr);
        })(),
        // Use contractedEndDate if available, fallback to plannedEndDate
        // Backend returns contractedEndDate for phases
        contractedEndDate: (() => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const dateStr = phase.contractedEndDate || (phase as any).plannedEndDate;
          if (!dateStr) return null;
          return parseDateStringLocal(dateStr);
        })(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        contractedDuration: phase.contractedDurationHours || (phase as any).plannedDurationHours || 0,
        actualStartDate: parseDateStringLocal(phase.actualStartDate),
        actualEndDate: parseDateStringLocal(phase.actualEndDate),
        actualDuration: phase.actualDurationHours || 0,
        tasks,
        displayOrder: phase.displayOrder,
        status: phase.status as ExecutionStatus,
        kpiScore: phase.kpiScore || 0,
      };
    });

    // Extract currentProjectDate (lock date) from API response, fallback to projectStartDate if not set
    const currentProjectDate = apiResponse.currentProjectDate 
      ? parseDateStringLocal(apiResponse.currentProjectDate)
      : (apiResponse.projectStartDate 
          ? parseDateStringLocal(apiResponse.projectStartDate)
          : null);
    
    
    return {
      projectId: apiResponse.projectId,
      projectName: apiResponse.projectName,
      projectStartDate: apiResponse.projectStartDate 
        ? parseDateStringLocal(apiResponse.projectStartDate) || dateToLocalDateString(new Date())
        : dateToLocalDateString(new Date()),
      currentProjectDate: currentProjectDate || undefined, // Date when project was locked (for date simulation)
      phases,
      changeOrders: [], // Will be loaded separately if needed
      auditLog: [], // Will be loaded separately if needed
    };
  };

  // Load execution plan from API
  const loadExecutionPlan = async (projectId?: number) => {
    if (!projectId) {
      // No projectId - fallback to localStorage or mock
      const savedExecution = localStorage.getItem('executionPlan');
      if (savedExecution) {
        try {
          const parsed = JSON.parse(savedExecution);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const parseDates = (obj: any): any => {
            if (obj === null || typeof obj !== 'object') return obj;
            if (Array.isArray(obj)) return obj.map(parseDates);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const result: any = {};
            for (const key in obj) {
              if (key.includes('Date') && obj[key]) {
                // Handle date strings - append T00:00:00 if it's a date-only string to avoid timezone issues
                const dateValue = obj[key];
                if (typeof dateValue === 'string') {
                  result[key] = dateValue.includes('T') 
                    ? new Date(dateValue) 
                    : new Date(dateValue + 'T00:00:00');
                } else {
                  result[key] = new Date(dateValue);
                }
              } else if (key === 'phases' || key === 'tasks' || key === 'actions' || key === 'changeOrders' || key === 'auditLog') {
                result[key] = parseDates(obj[key]);
              } else {
                result[key] = parseDates(obj[key]);
              }
            }
            return result;
          };
          setExecutionPlan(parseDates(parsed));
        } catch (error) {
          console.error('Error loading execution plan from localStorage:', error);
          setExecutionPlan(convertPlanToExecution(mockProjectPlan));
        }
      } else {
        setExecutionPlan(convertPlanToExecution(mockProjectPlan));
      }
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setApiError(null);
      
      // Load project metadata to get project name, libraryName, siteId, and status
      let projectName = '';
      try {
        const project = await getProject(projectId);
        // Unlocked (PLANNING) projects must not open in Execution; redirect to Planning
        if (project.status === 'PLANNING') {
          navigate(`/planning?projectId=${projectId}`);
          setIsLoading(false);
          return;
        }
        projectName = project.name || '';
        setProjectLibraryName(project.libraryName || null);
        setProjectSiteId(project.rootSiteId || null);
        setProjectStatus(project.status || null);
      } catch (error) {
        console.warn('Failed to load project metadata:', error);
        // Continue without project name - will use fallback
      }
      
      // Try to get execution plan
      try {
        const apiResponse = await getExecutionPlan(projectId);
        const plan = convertApiResponseToExecutionPlan(apiResponse);
        // Ensure project name is set from project metadata if execution plan doesn't have it
        if (!plan.projectName && projectName) {
          plan.projectName = projectName;
        }
        
        // Load task assignments for all tasks
        await loadTaskAssignments(plan);
        
            // Set initial project date from currentProjectDate (lock date) if available
            if (plan.currentProjectDate) {
              setProjectDate(plan.currentProjectDate);
            } else {
              // Fallback to projectStartDate if currentProjectDate is not set
              if (plan.projectStartDate) {
                setProjectDate(plan.projectStartDate);
              }
            }
        
        setExecutionPlan(plan);
      } catch (error) {
        const apiError = error as PlanningExecutionApiError;
        // If execution plan doesn't exist (404), try to create it
        if (apiError.code === 'NOT_FOUND') {
          try {
            await createExecutionPlan(projectId);
            // Retry getting the execution plan
            const apiResponse = await getExecutionPlan(projectId);
            const plan = convertApiResponseToExecutionPlan(apiResponse);
            // Ensure project name is set from project metadata if execution plan doesn't have it
            if (!plan.projectName && projectName) {
              plan.projectName = projectName;
            }
            
            // Load task assignments for all tasks
            await loadTaskAssignments(plan);
            
            // Set initial project date from currentProjectDate (lock date) if available
            if (plan.currentProjectDate) {
              setProjectDate(plan.currentProjectDate);
            } else {
              // Fallback to projectStartDate if currentProjectDate is not set
              if (plan.projectStartDate) {
                setProjectDate(plan.projectStartDate);
              }
            }
            
            setExecutionPlan(plan);
            setSnackbar({
              open: true,
              message: 'Execution plan created from planning data',
              severity: 'success',
            });
          } catch (createError) {
            const createApiError = createError as PlanningExecutionApiError;
            setApiError(createApiError.message || 'Failed to create execution plan');
            console.error('Error creating execution plan:', createError);
            // Fallback to localStorage
            const savedExecution = localStorage.getItem(`executionPlan-${projectId}`);
            if (savedExecution) {
              try {
                const parsed = JSON.parse(savedExecution);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const parseDates = (obj: any): any => {
                  if (obj === null || typeof obj !== 'object') return obj;
                  if (Array.isArray(obj)) return obj.map(parseDates);
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const result: any = {};
                  for (const key in obj) {
                    if (key.includes('Date') && obj[key]) {
                      result[key] = new Date(obj[key]);
                    } else {
                      result[key] = parseDates(obj[key]);
                    }
                  }
                  return result;
                };
                setExecutionPlan(parseDates(parsed));
              } catch (e) {
                console.error('Error loading from localStorage:', e);
              }
            }
          }
        } else {
          setApiError(apiError.message || 'Failed to load execution plan');
          console.error('Error loading execution plan:', error);
          // Fallback to localStorage
          const savedExecution = localStorage.getItem(`executionPlan-${projectId}`);
          if (savedExecution) {
            try {
              const parsed = JSON.parse(savedExecution);
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const parseDates = (obj: any): any => {
                if (obj === null || typeof obj !== 'object') return obj;
                if (Array.isArray(obj)) return obj.map(parseDates);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const result: any = {};
                for (const key in obj) {
                  if (key.includes('Date') && obj[key]) {
                    result[key] = new Date(obj[key]);
                  } else {
                    result[key] = parseDates(obj[key]);
                  }
                }
                return result;
              };
              setExecutionPlan(parseDates(parsed));
            } catch (e) {
              console.error('Error loading from localStorage:', e);
            }
          }
        }
      }
    } catch (error) {
      const apiError = error as ProjectApiError | PlanningExecutionApiError;
      setApiError(apiError.message || 'Failed to load execution plan');
      console.error('Error loading execution plan:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadChangeOrders = async (projectId: number) => {
    try {
      const res = await getProjectChangeOrders(projectId);
      setChangeOrdersList(res.changeOrders || []);
    } catch (err) {
      console.error('Error loading change orders:', err);
      setChangeOrdersList([]);
    }
  };

  // Load task assignments for all tasks in the execution plan
  const loadTaskAssignments = async (plan: ExecutionPlan) => {
    try {
      // Collect all task IDs from all phases
      const taskIds: number[] = [];
      plan.phases.forEach(phase => {
        phase.tasks.forEach(task => {
          taskIds.push(task.taskId);
        });
      });

      // Load assignments for all tasks in parallel
      const assignmentPromises = taskIds.map(async (taskId) => {
        try {
          const assignments = await getTaskAssignments(taskId);
          
          // Extract email addresses from assignments
          const userEmails = assignments.map((a) => {
            return a.user?.email || '';
          }).filter(email => email);
          
          return { taskId, userEmails };
        } catch (error) {
          console.error(`loadTaskAssignments: Failed to load assignments for task ${taskId}:`, error);
          return { taskId, userEmails: [] };
        }
      });

      const assignmentResults = await Promise.all(assignmentPromises);

      // Update the plan with loaded assignments
      plan.phases.forEach(phase => {
        phase.tasks.forEach(task => {
          const assignmentResult = assignmentResults.find(r => r.taskId === task.taskId);
          if (assignmentResult) {
            task.assignedUsers = assignmentResult.userEmails;
          }
        });
      });
    } catch (error) {
      console.error('loadTaskAssignments: Error loading task assignments:', error);
      console.error('loadTaskAssignments: Error details:', {
        error,
        errorType: typeof error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined
      });
      // Don't throw - assignments are optional
    }
  };

  // Load organization stats
  const loadOrganizationStats = async (projectId?: number) => {
    if (!projectId || isNaN(projectId)) {
      return;
    }
    
    try {
      setOrganizationStatsLoading(true);
      // Ensure projectId is a number
      const numericProjectId = typeof projectId === 'string' ? parseInt(projectId, 10) : projectId;
      if (isNaN(numericProjectId)) {
        console.error('loadOrganizationStats: Invalid projectId after conversion:', projectId);
        setOrganizationStats([]);
        return;
      }
      
      const stats = await getOrganizationStats(numericProjectId);
      
      if (!stats.organizations || !Array.isArray(stats.organizations)) {
        console.warn('loadOrganizationStats: Organizations is not an array or is missing:', stats.organizations);
      }
      
      const organizationsToSet = stats.organizations || [];
      setOrganizationStats(organizationsToSet);
    } catch (error) {
      console.error('loadOrganizationStats: Failed to load organization stats:', error);
      // Don't show error to user - stats are optional
      setOrganizationStats([]);
    } finally {
      setOrganizationStatsLoading(false);
    }
  };

  // Load execution plan on mount or when projectId / refresh changes (refresh=1 used after reject to show rollback)
  const refreshParam = searchParams.get('refresh');
  useEffect(() => {
    const projectId = urlProjectId ? parseInt(urlProjectId, 10) : undefined;
    if (projectId && !isNaN(projectId)) {
      loadExecutionPlan(projectId);
      if (isTenantOrClientUser(user)) {
        setOrganizationStats([]);
        setOrganizationStatsLoading(false);
      } else {
        loadOrganizationStats(projectId);
      }
      if (refreshParam === '1') {
        navigate(`/execution?projectId=${projectId}`, { replace: true });
      }
    } else {
      loadExecutionPlan();
    }
  }, [urlProjectId, refreshParam, user]);

  // Load change orders when Change Orders tab is selected
  useEffect(() => {
    if (selectedTab === 1 && urlProjectId && !isNaN(parseInt(urlProjectId, 10))) {
      loadChangeOrders(parseInt(urlProjectId, 10));
    }
  }, [selectedTab, urlProjectId]);

  // Save execution plan to localStorage (fallback)
  const saveExecutionPlan = useCallback((plan: ExecutionPlan) => {
    if (plan.projectId) {
      localStorage.setItem(`executionPlan-${plan.projectId}`, JSON.stringify(plan));
    }
    localStorage.setItem('executionPlan', JSON.stringify(plan));
  }, []);

  // Show loading screen while loading, or if no execution plan and no error
  // If there's an error and we're not loading, show error message
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LinearProgress sx={{ width: '50%', mb: 2 }} />
        <Typography sx={{ fontWeight: 'bold', color: 'white' }}>Loading execution plan...</Typography>
      </Box>
    );
  }

  // If there's an error and no execution plan, show error
  if (!executionPlan && apiError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3 }}>
        <Typography sx={{ fontWeight: 'bold', color: 'error.main', mb: 2, fontSize: '1.2rem' }}>
          Failed to Load Execution Plan
        </Typography>
        <Typography sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: '600px', mb: 2 }}>
          {apiError}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: '600px' }}>
          Please check your authentication and try refreshing the page.
        </Typography>
      </Box>
    );
  }

  // If still no execution plan (but no error), show loading
  if (!executionPlan) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LinearProgress sx={{ width: '50%', mb: 2 }} />
        <Typography sx={{ fontWeight: 'bold', color: 'white' }}>Loading execution plan...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: 'white', backgroundImage: 'none' }}>
      <AppBar position="static">
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            {isPublicExecutionUser ? (
              <Typography variant="h6" component="div" sx={{ color: 'inherit', fontWeight: 600 }}>
                {executionPlan?.projectName ?? 'Project'}
              </Typography>
            ) : (
            <ProjectSelector
              selectedProjectId={urlProjectId ? parseInt(urlProjectId, 10) : null}
              onProjectChange={(projectId) => {
                navigate(`/execution?projectId=${projectId}`);
              }}
              onCreateNew={() => {
                navigate('/planning');
              }}
              showCreateButton={!urlProjectId} // Hide "New Project" button when viewing a specific project
              showListButton={true}
              context="execution"
            />
            )}
          </Box>
          {/* Documents drawer trigger: icon-only, larger, in toolbar */}
          {urlProjectId && !isNaN(parseInt(urlProjectId, 10)) && (
            <Tooltip title="Documents">
              <IconButton
                color="inherit"
                onClick={() => setDocumentsDrawerOpen(true)}
                aria-label="Open Documents"
                sx={{ ml: 1 }}
              >
                <Description sx={{ fontSize: 28 }} />
              </IconButton>
            </Tooltip>
          )}
          {/* Project Report: Official/Complete report (System Admin, Exec Admin, Airport Manager) */}
          {urlProjectId && !isNaN(parseInt(urlProjectId, 10)) && user && (hasRole(user, 'System Admin') || hasRole(user, 'Exec Admin') || hasRole(user, 'Airport Manager')) && (
            <Tooltip title="Project Report">
              <IconButton
                color="inherit"
                onClick={() => navigate(`/audit-report?projectId=${urlProjectId}`)}
                aria-label="Project Report"
                sx={{ ml: 1 }}
              >
                <Assessment sx={{ fontSize: 28 }} />
              </IconButton>
            </Tooltip>
          )}
          {/* Save as Template hidden in UI; remove display: 'none' to show again */}
          <Box sx={{ display: 'none' }}>
            {urlProjectId && (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<ContentCopy />}
                onClick={() => setShowSaveTemplateDialog(true)}
                sx={{ ml: 2 }}
              >
                Save as Template
              </Button>
            )}
          </Box>
          {/* Unlock project hidden in UI; remove display: 'none' to show again */}
          <Box sx={{ display: 'none' }}>
            {user && hasRole(user, 'System Admin') && projectStatus === 'EXECUTION' && urlProjectId && (
              <Tooltip title="Return project to Planning and clear execution dates. Next lock will recreate the execution plan.">
                <span>
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={isUnlocking ? undefined : <LockOpen />}
                    onClick={() => setUnlockConfirmOpen(true)}
                    disabled={isUnlocking}
                    sx={{ ml: 2 }}
                  >
                    {isUnlocking ? 'Unlocking…' : 'Unlock project'}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Unlock project confirmation */}
      <Dialog open={unlockConfirmOpen} onClose={() => !isUnlocking && setUnlockConfirmOpen(false)}>
        <DialogTitle>Unlock project?</DialogTitle>
        <DialogContent>
          <Typography>
            This will return the project to Planning and clear Contracted/Actual dates. You can lock it again to recreate the execution plan. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnlockConfirmOpen(false)} disabled={isUnlocking}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (!urlProjectId) return;
              const projectId = parseInt(urlProjectId, 10);
              if (isNaN(projectId)) return;
              try {
                setIsUnlocking(true);
                await unlockProject(projectId);
                setUnlockConfirmOpen(false);
                setSnackbar({
                  open: true,
                  message: 'Project unlocked and returned to Planning.',
                  severity: 'success',
                });
                navigate(`/planning?projectId=${projectId}`);
              } catch (err) {
                const apiErr = err as ProjectApiError;
                setSnackbar({
                  open: true,
                  message: apiErr.message || 'Failed to unlock project',
                  severity: 'error',
                });
              } finally {
                setIsUnlocking(false);
              }
            }}
            disabled={isUnlocking}
          >
            {isUnlocking ? 'Unlocking…' : 'Unlock'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ p: 3 }}>
        {apiError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError(null)}>
            {apiError}
          </Alert>
        )}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4">
            {executionPlan?.projectName || 'Project Execution'}
          </Typography>
          {executionPlan?.projectStartDate && (
            <Typography variant="body1" color="text.secondary">
              Start Date: {(() => {
                const date = dateStringToLocalDate(executionPlan.projectStartDate);
                return date ? format(date, 'MMM dd, yyyy') : executionPlan.projectStartDate;
              })()}
            </Typography>
          )}
        </Box>

        {!isPublicExecutionUser && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Project Execution & Tracking view. Contracted dates are frozen from planning.
          Track actual performance and view KPI scores. Changes are saved automatically to the API.
        </Alert>
        )}

        {/* Document Viewer (Export to Excel removed) */}
        {!isPublicExecutionUser && urlProjectId && projectLibraryName && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              startIcon={<Description />}
              onClick={() => setViewerOpen(true)}
            >
              Open Document Viewer
            </Button>
          </Box>
        )}

        {/* Project Date Controls */}
        {!isPublicExecutionUser && urlProjectId && (
          <ProjectDateControls
            projectId={parseInt(urlProjectId, 10)}
            initialProjectDate={executionPlan?.currentProjectDate || projectDate || null}
            onDateChange={(newDate) => {
              setProjectDate(newDate);
            }}
          />
        )}

        {!isPublicExecutionUser && (
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => {
            setSelectedTab(newValue);
            if (!documentsDrawerPinned) setDocumentsDrawerOpen(false);
          }}
          sx={{ mb: 3 }}
        >
          <Tab label="Execution View" />
          <Tab label="Change Orders" />
          <Tab label="KPI Dashboard" />
          <Tab label="Audit Log" />
          <Tab label="Project Users" />
        </Tabs>
        )}

        {isPublicExecutionUser && executionPlan && (
          <ExecutionView 
            executionPlan={executionPlan}
            setExecutionPlan={setExecutionPlan}
            onSave={saveExecutionPlan}
            onShowChangeOrder={(item) => setChangeOrderDialog({ open: true, item })}
            organizationStats={organizationStats}
            organizationStatsLoading={organizationStatsLoading}
            projectDate={projectDate}
            publicMode
            onSnackbar={setSnackbar}
          />
        )}

        {!isPublicExecutionUser && selectedTab === 0 && executionPlan && (
          <>
            <ExecutionView 
              executionPlan={executionPlan}
              setExecutionPlan={setExecutionPlan}
              onSave={saveExecutionPlan}
              onShowChangeOrder={(item) => setChangeOrderDialog({ open: true, item })}
              organizationStats={organizationStats}
              organizationStatsLoading={organizationStatsLoading}
              projectDate={projectDate}
              onSnackbar={setSnackbar}
            />
            <GanttChartView {...executionPlanToGanttData(executionPlan)} title="Gantt Chart" />
          </>
        )}

        {!isPublicExecutionUser && selectedTab === 1 && urlProjectId && !isNaN(parseInt(urlProjectId, 10)) && (
          <ChangeOrdersView 
            projectId={parseInt(urlProjectId, 10)}
            changeOrders={changeOrdersList}
            canManipulateChangeOrders={isFraportUser(user)}
            onRefresh={async () => {
              await loadExecutionPlan(parseInt(urlProjectId, 10));
              await loadChangeOrders(parseInt(urlProjectId, 10));
            }}
            onApprove={(_id) => {
              setSnackbar({ open: true, message: 'Change order approval not yet implemented', severity: 'info' });
            }}
            onReject={(_id) => {
              setSnackbar({ open: true, message: 'Change order rejection not yet implemented', severity: 'info' });
            }}
            onSnackbar={setSnackbar}
          />
        )}

        {!isPublicExecutionUser && selectedTab === 2 && (
          <KPIDashboard executionPlan={executionPlan} />
        )}

        {!isPublicExecutionUser && selectedTab === 3 && urlProjectId && !isNaN(parseInt(urlProjectId, 10)) && (
          <AuditLogView projectId={parseInt(urlProjectId, 10)} />
        )}

        {!isPublicExecutionUser && selectedTab === 4 && urlProjectId && (
          <ProjectUserManagement projectId={parseInt(urlProjectId, 10)} />
        )}

        {/* Documents drawer (UX-4-F): right-side drawer with pin; reference TaskViewPanel */}
        {urlProjectId && !isNaN(parseInt(urlProjectId, 10)) && (
          <Drawer
            anchor="right"
            open={documentsDrawerOpen}
            onClose={() => {
              if (!documentsDrawerPinned) setDocumentsDrawerOpen(false);
              setDocumentsDrawerError(null);
            }}
            ModalProps={{
              hideBackdrop: documentsDrawerPinned,
              disableEnforceFocus: documentsDrawerPinned,
              BackdropProps: {
                sx: {
                  pointerEvents: documentsDrawerPinned ? 'none' : 'auto',
                },
              },
            }}
            PaperProps={{
              sx: {
                width: { xs: '100%', sm: '40%', md: '500px' },
                minWidth: '500px',
                maxWidth: '800px',
              },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Documents
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip title={documentsDrawerPinned ? 'Unpin (close when switching tabs)' : 'Pin (keep open when switching tabs)'}>
                    <IconButton
                      size="small"
                      color={documentsDrawerPinned ? 'primary' : 'default'}
                      onClick={() => setDocumentsDrawerPinned((p) => !p)}
                    >
                      <PushPin sx={{ transform: documentsDrawerPinned ? undefined : 'rotate(-45deg)' }} />
                    </IconButton>
                  </Tooltip>
                  <IconButton onClick={() => setDocumentsDrawerOpen(false)} size="small">
                    <Close />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column' }}>
                {documentsDrawerError && (
                  <Alert
                    severity="error"
                    onClose={() => setDocumentsDrawerError(null)}
                    sx={{ mb: 2 }}
                  >
                    {documentsDrawerError}
                  </Alert>
                )}
                <Tabs value={documentsDrawerTab} onChange={(_, v) => setDocumentsDrawerTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tab icon={<Cloud />} iconPosition="start" label="SharePoint" id="doc-tab-sp" aria-controls="doc-tabpanel-sp" />
                  <Tab icon={<Storage />} iconPosition="start" label="Large Files" id="doc-tab-large" aria-controls="doc-tabpanel-large" />
                </Tabs>
                {documentsDrawerTab === 0 && (
                  <LibraryBrowser
                    projectId={parseInt(urlProjectId, 10)}
                    externalDocumentsMode={isPublicExecutionUser}
                    onFileSelect={async (file: FileMetadata) => {
                      setDocumentsDrawerError(null);
                      if (shouldOpenSharePointDirectly(user) && file.webUrl) {
                        window.open(file.webUrl, '_blank');
                        return;
                      }
                      if (file.itemId) {
                        try {
                          const blob = await apiService.downloadProjectSharePointFile(
                            parseInt(urlProjectId, 10),
                            String(file.itemId)
                          );
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = file.name;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error: unknown) {
                          console.error('Failed to download file:', error);
                          let message = 'Failed to download file';
                          if (error && typeof error === 'object' && 'response' in error) {
                            const res = (error as { response?: { data?: unknown; status?: number } }).response;
                            if (res?.status === 404) message = 'File not found in library';
                            else if (res?.data && typeof res.data === 'object' && res.data !== null && 'message' in res.data && typeof (res.data as { message: string }).message === 'string') {
                              message = (res.data as { message: string }).message;
                            }
                          }
                          setDocumentsDrawerError(message);
                        }
                      } else {
                        setDocumentsDrawerError('File information is incomplete');
                      }
                    }}
                  />
                )}
                {documentsDrawerTab === 1 && (
                  <LargeFilesBrowser
                    projectId={parseInt(urlProjectId, 10)}
                  />
                )}
              </Box>
            </Box>
          </Drawer>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {urlProjectId && (
        <SaveTemplateDialog
          open={showSaveTemplateDialog}
          onClose={() => setShowSaveTemplateDialog(false)}
          projectId={parseInt(urlProjectId, 10)}
          onSuccess={(_templateId) => {
            setSnackbar({
              open: true,
              message: 'Template created successfully',
              severity: 'success',
            });
          }}
        />
      )}

      {/* Document Viewer Drawer */}
      {projectLibraryName && urlProjectId && (
        <Drawer
          anchor="right"
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          PaperProps={{ sx: { width: '40%', minWidth: 400 } }}
        >
          <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">SharePoint Document Viewer</Typography>
              <IconButton onClick={() => setViewerOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <SharePointDocumentViewer
                projectId={parseInt(urlProjectId, 10)}
                siteId={projectSiteId || DEFAULT_SITE_ID}
                libraryName={projectLibraryName}
                initialFolderPath={viewerFolderPath}
                onDocumentSelect={(document) => {
                  if (shouldOpenSharePointDirectly(user) && document.webUrl) {
                    window.open(document.webUrl, '_blank', 'noopener,noreferrer');
                    return;
                  }
                  if (document.webUrl) {
                    window.open(document.webUrl, '_blank', 'noopener,noreferrer');
                  } else {
                    console.warn('Document does not have a webUrl:', document);
                  }
                }}
              />
            </Box>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

// EPC-F1/EPC-F2: Task/phase rollups (actualStartDate, actualEndDate, status, kpiScore) come from backend only.
// Removed recalculateTaskKPIs/recalculatePhaseKPIs; refetch execution plan after updates to get backend-calculated dates.

// Execution View Component
type ExecutionSnackbarPayload = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
};

interface ExecutionViewProps {
  executionPlan: ExecutionPlan;
  setExecutionPlan: (plan: ExecutionPlan) => void;
  onSave: (plan: ExecutionPlan) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onShowChangeOrder: (item: any) => void;
  organizationStats?: OrganizationStat[];
  organizationStatsLoading?: boolean;
  projectDate?: string | null;
  /** Tenant/Client: fixed columns, no org stats / status bar / column persistence */
  publicMode?: boolean;
  onSnackbar?: (payload: ExecutionSnackbarPayload) => void;
}

// Column definitions for Execution View
type ExecutionColumnKey = 
  | 'item' 
  | 'status' 
  | 'percentComplete' 
  | 'assigned' 
  | 'prerequisites' 
  | 'duration'
  | 'contractedStart' 
  | 'contractedEnd' 
  | 'actualStart' 
  | 'actualEnd' 
  | 'kpiScore' 
  | 'documentName'
  | 'instructions'
  | 'sourceDocuments'
  | 'actions';

interface ColumnConfig {
  key: ExecutionColumnKey;
  label: string;
  group?: string;
  priority: 'high' | 'medium' | 'low';
}

const EXECUTION_COLUMNS: ColumnConfig[] = [
  { key: 'item', label: 'Item', priority: 'high' },
  { key: 'status', label: 'Status', priority: 'high' },
  { key: 'percentComplete', label: '% Complete', priority: 'high' },
  { key: 'assigned', label: 'Assigned', priority: 'medium' },
  { key: 'prerequisites', label: 'Prerequisites', priority: 'low' },
  { key: 'documentName', label: 'Document Name', priority: 'low' },
  { key: 'instructions', label: 'Instructions', priority: 'low' },
  { key: 'sourceDocuments', label: 'Source documents', priority: 'low' },
  { key: 'duration', label: 'Duration', priority: 'medium' },
  { key: 'contractedStart', label: 'Contracted Start', group: 'dates', priority: 'high' },
  { key: 'contractedEnd', label: 'Contracted End', group: 'dates', priority: 'high' },
  { key: 'actualStart', label: 'Actual Start', group: 'dates', priority: 'high' },
  { key: 'actualEnd', label: 'Actual End', group: 'dates', priority: 'high' },
  { key: 'kpiScore', label: 'KPI Score', priority: 'high' },
  { key: 'actions', label: 'Actions', priority: 'high' },
];

const QUICK_VIEW_PRESETS: Record<string, Set<ExecutionColumnKey>> = {
  overview: new Set(['item', 'status', 'percentComplete', 'kpiScore', 'actions']),
  details: new Set(['item', 'status', 'percentComplete', 'assigned', 'prerequisites', 'duration', 'contractedStart', 'contractedEnd', 'actualStart', 'actualEnd', 'kpiScore', 'actions']),
  dates: new Set(['item', 'status', 'duration', 'contractedStart', 'contractedEnd', 'actualStart', 'actualEnd', 'kpiScore', 'actions']),
};

const PUBLIC_EXECUTION_COLUMN_SET = new Set<ExecutionColumnKey>([
  'item',
  'status',
  'percentComplete',
  'contractedStart',
  'contractedEnd',
  'actualStart',
  'actualEnd',
]);

const ExecutionView = ({ executionPlan, setExecutionPlan, onSave, onShowChangeOrder, organizationStats = [], organizationStatsLoading = false, projectDate = null, publicMode = false, onSnackbar }: ExecutionViewProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // @ts-ignore - intentionally unused
  const _isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [projectExpanded, setProjectExpanded] = useState<boolean>(true); // Project row expanded by default
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());

  // Default to expanded phases and tasks when execution plan first loads (and when plan changes, e.g. different project)
  const prevPlanKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!executionPlan?.phases?.length) return;
    const planKey = `${executionPlan.phases.map(p => p.phaseId).join(',')}`;
    if (prevPlanKeyRef.current === planKey) return;
    prevPlanKeyRef.current = planKey;
    setExpandedPhases(new Set(executionPlan.phases.map(p => p.phaseId.toString())));
    const taskIds = new Set<string>();
    executionPlan.phases.forEach(phase => {
      phase.tasks.forEach(task => taskIds.add(task.taskId.toString()));
    });
    setExpandedTasks(taskIds);
  }, [executionPlan]);

  // Column visibility state - load from localStorage or use default (public mode: fixed set, no persistence)
  const [visibleColumns, setVisibleColumns] = useState<Set<ExecutionColumnKey>>(() => {
    if (publicMode) {
      return new Set(PUBLIC_EXECUTION_COLUMN_SET);
    }
    const saved = localStorage.getItem('executionViewColumns');
    // Check if mobile using window width (600px is typical sm breakpoint)
    const checkIsMobile = typeof window !== 'undefined' && window.innerWidth < 600;
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved) as string[];
        const savedCols = new Set<ExecutionColumnKey>(parsed.filter((col): col is ExecutionColumnKey => 
          ['item', 'status', 'percentComplete', 'actualStart', 'actualEnd', 'kpiScore', 'actions', 'assigned', 'prerequisites', 'duration', 'contractedStart', 'contractedEnd', 'documentName', 'instructions', 'sourceDocuments'].includes(col)
        ));
        // Always ensure critical columns are visible: contractedStart and contractedEnd
        savedCols.add('contractedStart');
        savedCols.add('contractedEnd');
        // Ensure prerequisites is included if not mobile (add it to saved preferences)
        if (!checkIsMobile && !savedCols.has('prerequisites')) {
          savedCols.add('prerequisites');
        }
        return savedCols;
      } catch {
        // Fall through to default
      }
    }
    // Default: show high priority columns, hide low priority on mobile
    // Always include contractedStart and contractedEnd as they're critical for execution view
    const defaultCols = new Set<ExecutionColumnKey>(['item', 'status', 'percentComplete', 'contractedStart', 'contractedEnd', 'actualStart', 'actualEnd', 'kpiScore', 'actions']);
    if (!checkIsMobile) {
      defaultCols.add('assigned');
      defaultCols.add('prerequisites');
      defaultCols.add('duration');
    }
    return defaultCols;
  });
  
  useEffect(() => {
    if (publicMode) {
      setVisibleColumns(new Set(PUBLIC_EXECUTION_COLUMN_SET));
    }
  }, [publicMode]);

  // Update visible columns when screen size changes to ensure prerequisites is visible on desktop
  useEffect(() => {
    if (publicMode) return;
    if (!isMobile) {
      setVisibleColumns(prev => {
        const updated = new Set(prev);
        if (!updated.has('prerequisites')) {
          updated.add('prerequisites');
        }
        return updated;
      });
    }
  }, [isMobile, publicMode]);

  const [_columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  // Save column preferences to localStorage
  useEffect(() => {
    if (publicMode) return;
    localStorage.setItem('executionViewColumns', JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns, publicMode]);

  // Adjust visible columns based on screen size
  useEffect(() => {
    if (publicMode) return;
    if (isMobile) {
      // On mobile, hide less critical columns
      setVisibleColumns(prev => {
        const next = new Set(prev);
        next.delete('prerequisites');
        next.delete('contractedStart');
        next.delete('contractedEnd');
        if (!next.has('assigned')) {
          // Keep assigned if it was already visible
        }
        return next;
      });
    }
  }, [isMobile, publicMode]);

  // Helper function to get task name by ID
  const getTaskNameById = (taskId: string | number): string => {
    const taskIdNum = typeof taskId === 'string' ? parseInt(taskId, 10) : taskId;
    for (const phase of executionPlan.phases) {
      for (const task of phase.tasks) {
        if (task.taskId === taskIdNum) {
          return task.name;
        }
      }
    }
    return String(taskId);
  };

  // Calculate all task IDs and check if all are expanded
  const allTaskIds = useMemo(() => {
    const ids = new Set<string>();
    executionPlan.phases.forEach(phase => {
      phase.tasks.forEach(task => {
        ids.add(task.taskId.toString());
      });
    });
    return ids;
  }, [executionPlan]);

  const allTasksExpanded = useMemo(() => {
    return allTaskIds.size > 0 && expandedTasks.size === allTaskIds.size && 
      Array.from(allTaskIds).every(id => expandedTasks.has(id));
  }, [allTaskIds, expandedTasks]);

  // Expand/Collapse project row
  const handleToggleProject = () => {
    setProjectExpanded(prev => !prev);
  };

  // Expand/Collapse all phases
  const handleExpandCollapseAllPhases = () => {
    if (expandedPhases.size === executionPlan.phases.length && projectExpanded) {
      // Collapse all - also collapse project
      setExpandedPhases(new Set());
      setProjectExpanded(false);
    } else {
      // Expand all - also expand project
      setProjectExpanded(true);
      setExpandedPhases(new Set(executionPlan.phases.map(p => p.phaseId.toString())));
    }
  };

  // Expand/Collapse all tasks
  const handleExpandCollapseAllTasks = useCallback(() => {
    if (allTasksExpanded) {
      // Collapse all
      setExpandedTasks(new Set());
    } else {
      // Expand all
      setExpandedTasks(new Set(allTaskIds));
    }
  }, [allTasksExpanded, allTaskIds]);

  // Toggle phase expansion
  const handleTogglePhase = (phaseId: string) => {
    setExpandedPhases(prev => {
      const next = new Set(prev);
      if (next.has(phaseId)) {
        next.delete(phaseId);
      } else {
        next.add(phaseId);
      }
      return next;
    });
  };

  // Toggle task expansion
  const handleToggleTask = (taskId: string) => {
    setExpandedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  // Calculate status summary
  const statusSummary = useMemo(() => {
    let complete = 0;
    let inProgress = 0;
    let notStarted = 0;
    let locked = 0;

    executionPlan.phases.forEach(phase => {
      if (phase.status === 'COMPLETE' || phase.status === 'LOCKED') complete++;
      else if (phase.status === 'IN_PROGRESS') inProgress++;
      else notStarted++;

      phase.tasks.forEach(task => {
        if (task.status === 'COMPLETE' || task.status === 'LOCKED') complete++;
        else if (task.status === 'IN_PROGRESS') inProgress++;
        else notStarted++;

        task.actions.forEach(action => {
          if (action.status === 'COMPLETE' || action.status === 'LOCKED') {
            complete++;
            if (action.isLocked) locked++;
          }
          else if (action.status === 'IN_PROGRESS') inProgress++;
          else notStarted++;
        });
      });
    });

    return { complete, inProgress, notStarted, locked };
  }, [executionPlan]);

  // Toggle column visibility
  // @ts-ignore - intentionally unused
  const _toggleColumn = (columnKey: ExecutionColumnKey) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(columnKey)) {
        next.delete(columnKey);
      } else {
        next.add(columnKey);
      }
      return next;
    });
  };

  // Apply quick view preset
  // @ts-ignore - intentionally unused
  const _applyQuickView = (preset: string) => {
    if (QUICK_VIEW_PRESETS[preset]) {
      setVisibleColumns(new Set(QUICK_VIEW_PRESETS[preset]));
    }
    setColumnMenuAnchor(null);
  };

  // Get status icon
  const getStatusIcon = (status: ExecutionStatus) => {
    switch (status) {
      case 'COMPLETE':
      case 'LOCKED':
        return <CheckCircle sx={{ fontSize: 18, color: '#4caf50' }} />;
      case 'IN_PROGRESS':
        return <Schedule sx={{ fontSize: 18, color: '#ff9800' }} />;
      case 'NOT_STARTED':
        return <RadioButtonUnchecked sx={{ fontSize: 18, color: '#9e9e9e' }} />;
      default:
        return null;
    }
  };

  // Get KPI trend arrow
  // Negative KPI = reclaimed time (ahead) = GREEN down arrow
  // Positive KPI = overdue (delayed) = RED up arrow
  const getKPITrend = (kpi: KPIScore) => {
    if (kpi < 0) return <ArrowDownward sx={{ fontSize: 14, color: '#4caf50' }} />; // Reclaimed time (ahead) = GREEN
    if (kpi > 0) return <ArrowUpward sx={{ fontSize: 14, color: '#f44336' }} />; // Overdue (delayed) = RED
    return <ArrowForward sx={{ fontSize: 14, color: '#9e9e9e' }} />; // On time
  };

  // Get row border color based on status
  // Negative KPI = reclaimed time (ahead) = GREEN border
  // Positive KPI = overdue (delayed) = RED border
  const getRowBorderColor = (status: ExecutionStatus, kpi: KPIScore): string => {
    if (status === 'COMPLETE' || status === 'LOCKED') return '#4caf50';
    if (status === 'IN_PROGRESS') return '#ff9800';
    if (kpi < 0) return '#4caf50'; // Reclaimed time (negative = ahead) = GREEN
    if (kpi > 0) return '#f44336'; // Overdue (positive = delayed) = RED
    return 'transparent';
  };

  // Get row background tint
  const getRowBackgroundTint = (status: ExecutionStatus): string => {
    if (status === 'COMPLETE' || status === 'LOCKED') return 'rgba(76, 175, 80, 0.05)';
    if (status === 'IN_PROGRESS') return 'rgba(255, 152, 0, 0.05)';
    return 'transparent';
  };

  return (
    <Box>
      {!publicMode && (
      <>
      {/* Organization Stats - at the top of Execution View */}
      <OrganizationStats 
        organizations={organizationStats} 
        loading={organizationStatsLoading}
      />

      {/* Status Summary Bar */}
      <Paper sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mr: 2 }}>Status Summary:</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle sx={{ fontSize: 18, color: '#4caf50' }} />
            <Typography variant="body2">{statusSummary.complete} Complete</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Schedule sx={{ fontSize: 18, color: '#ff9800' }} />
            <Typography variant="body2">{statusSummary.inProgress} In Progress</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RadioButtonUnchecked sx={{ fontSize: 18, color: '#9e9e9e' }} />
            <Typography variant="body2">{statusSummary.notStarted} Not Started</Typography>
          </Box>
          {statusSummary.locked > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Lock sx={{ fontSize: 18, color: '#757575' }} />
              <Typography variant="body2">{statusSummary.locked} Locked</Typography>
            </Box>
          )}
        </Box>
      </Paper>
      </>
      )}

      {/* Controls Bar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={expandedPhases.size === executionPlan.phases.length && projectExpanded ? <UnfoldLess /> : <UnfoldMore />}
          onClick={handleExpandCollapseAllPhases}
        >
          Phases
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={allTasksExpanded ? <UnfoldLess /> : <UnfoldMore />}
          onClick={handleExpandCollapseAllTasks}
        >
          Tasks
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {EXECUTION_COLUMNS.filter(col => visibleColumns.has(col.key)).map(col => (
                <TableCell 
                  key={col.key}
                  sx={{ 
                    fontWeight: 'bold', 
                    backgroundColor: '#f5f5f5',
                    display: { xs: col.priority === 'low' ? 'none' : 'table-cell', sm: 'table-cell' }
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Project Row */}
            <ProjectRow
              executionPlan={executionPlan}
              expanded={projectExpanded}
              onToggleExpand={handleToggleProject}
              visibleColumns={visibleColumns}
              getStatusIcon={getStatusIcon}
              getKPITrend={getKPITrend}
              getRowBorderColor={getRowBorderColor}
              getRowBackgroundTint={getRowBackgroundTint}
            />
            {/* Phase Rows - only show if project is expanded */}
            {projectExpanded && executionPlan.phases.map(phase => (
              <PhaseRow 
                key={phase.phaseId} 
                phase={phase}
                executionPlan={executionPlan}
                setExecutionPlan={setExecutionPlan}
                onSave={onSave}
                onShowChangeOrder={onShowChangeOrder}
                expanded={expandedPhases.has(phase.phaseId.toString())}
                onToggleExpand={() => handleTogglePhase(phase.phaseId.toString())}
                expandedTasks={expandedTasks}
                onToggleTask={handleToggleTask}
                getTaskNameById={getTaskNameById}
                visibleColumns={visibleColumns}
                getStatusIcon={getStatusIcon}
                getKPITrend={getKPITrend}
                getRowBorderColor={getRowBorderColor}
                getRowBackgroundTint={getRowBackgroundTint}
                projectDate={projectDate}
                onSnackbar={onSnackbar}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

function formatActualStartForDisplay(status: ExecutionStatus | string | undefined, value: string | null | undefined): string {
  if (!status || status === 'NOT_STARTED' || !value) return '—';
  const date = dateStringToLocalDate(value);
  return date ? format(date, 'MMM dd, yyyy') : '—';
}

// Project Row Component
interface ProjectRowProps {
  executionPlan: ExecutionPlan;
  expanded: boolean;
  onToggleExpand: () => void;
  visibleColumns: Set<ExecutionColumnKey>;
  getStatusIcon: (status: ExecutionStatus) => React.ReactNode;
  getKPITrend: (kpi: KPIScore) => React.ReactNode;
  getRowBorderColor: (status: ExecutionStatus, kpi: KPIScore) => string;
  getRowBackgroundTint: (status: ExecutionStatus) => string;
}

const ProjectRow = ({ executionPlan, expanded, onToggleExpand, visibleColumns, getStatusIcon, getKPITrend, getRowBorderColor, getRowBackgroundTint }: ProjectRowProps) => {
  const projectStatus = calculateProjectStatus(executionPlan);
  const projectPercentComplete = calculateProjectPercentComplete(executionPlan);
  const projectKPI = calculateProjectKPI(executionPlan);
  const contractedDates = getProjectContractedDates(executionPlan);
  const actualDates = getProjectActualDates(executionPlan);
  const borderColor = getRowBorderColor(projectStatus, projectKPI);
  const bgTint = getRowBackgroundTint(projectStatus);

  // Calculate total duration from phases
  const totalDuration = executionPlan.phases.reduce((sum, phase) => {
    return sum + (phase.contractedDuration || 0);
  }, 0);

  return (
    <TableRow 
      sx={{ 
        backgroundColor: '#bbdefb',
        borderLeft: `4px solid ${borderColor}`,
        backgroundImage: bgTint !== 'transparent' ? `linear-gradient(to right, ${bgTint}, ${bgTint})` : 'none',
        fontWeight: 'bold',
      }}
    >
      {visibleColumns.has('item') && (
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton size="small" onClick={onToggleExpand}>
              {expanded ? <ExpandMore /> : <ChevronRight />}
            </IconButton>
            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>
              {executionPlan.projectName}
            </Typography>
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('status') && (
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(projectStatus)}
            <Chip 
              label={projectStatus} 
              size="small" 
              color={getStatusColor(projectStatus)}
            />
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('percentComplete') && (
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 80,
                height: 10,
                backgroundColor: 'grey.300',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${projectPercentComplete}%`,
                  height: '100%',
                  backgroundColor: projectPercentComplete === 100 ? '#4caf50' : '#2196f3',
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {projectPercentComplete}%
            </Typography>
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('assigned') && (
        <TableCell>
          {/* Project level - no assigned users */}
        </TableCell>
      )}
      {visibleColumns.has('prerequisites') && (
        <TableCell>
          {/* Project level - no prerequisites */}
        </TableCell>
      )}
      {visibleColumns.has('documentName') && <TableCell>{'\u00A0'}</TableCell>}
      {visibleColumns.has('instructions') && <TableCell>{'\u00A0'}</TableCell>}
      {visibleColumns.has('sourceDocuments') && <TableCell>{'\u00A0'}</TableCell>}
      {visibleColumns.has('duration') && (
        <TableCell>
          <Typography variant="body2">
            {totalDuration > 0 ? `${(totalDuration / 8).toFixed(1)} days` : '-'}
          </Typography>
        </TableCell>
      )}
      {visibleColumns.has('contractedStart') && (
        <TableCell>
          <Typography variant="body2">
            {contractedDates.start ? (() => {
              const date = dateStringToLocalDate(contractedDates.start);
              return date ? format(date, 'MMM dd, yyyy') : '-';
            })() : '-'}
          </Typography>
        </TableCell>
      )}
      {visibleColumns.has('contractedEnd') && (
        <TableCell>
          <Typography variant="body2">
            {contractedDates.end ? (() => {
              const date = dateStringToLocalDate(contractedDates.end);
              return date ? format(date, 'MMM dd, yyyy') : '-';
            })() : '-'}
          </Typography>
        </TableCell>
      )}
      {visibleColumns.has('actualStart') && (
        <TableCell>
          <Typography variant="body2">
            {actualDates.start ? (() => {
              const date = dateStringToLocalDate(actualDates.start);
              return date ? format(date, 'MMM dd, yyyy') : '-';
            })() : '-'}
          </Typography>
        </TableCell>
      )}
      {visibleColumns.has('actualEnd') && (
        <TableCell>
          <Typography variant="body2">
            {actualDates.end ? (() => {
              const date = dateStringToLocalDate(actualDates.end);
              return date ? format(date, 'MMM dd, yyyy') : '-';
            })() : '-'}
          </Typography>
        </TableCell>
      )}
      {visibleColumns.has('kpiScore') && (
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getKPITrend(projectKPI)}
            <Typography 
              variant="body2" 
              sx={{ 
                // Negative KPI = reclaimed time (ahead) = GREEN, Positive KPI = overdue (delayed) = RED
                color: projectKPI < 0 ? '#4caf50' : projectKPI > 0 ? '#f44336' : '#9e9e9e',
                fontWeight: 'bold',
              }}
            >
              {projectKPI > 0 ? `+${projectKPI} days` : projectKPI < 0 ? `${projectKPI} days` : 'On Time'}
            </Typography>
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('actions') && (
        <TableCell>
          <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>—</Typography>
        </TableCell>
      )}
    </TableRow>
  );
};

// Phase Row Component
interface PhaseRowProps {
  phase: ExecutionPhase;
  executionPlan: ExecutionPlan;
  setExecutionPlan: (plan: ExecutionPlan) => void;
  onSave: (plan: ExecutionPlan) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onShowChangeOrder: (item: any) => void;
  expanded: boolean;
  onToggleExpand: () => void;
  expandedTasks: Set<string>;
  onToggleTask: (taskId: string) => void;
  getTaskNameById: (taskId: string) => string;
  visibleColumns: Set<ExecutionColumnKey>;
  getStatusIcon: (status: ExecutionStatus) => React.ReactNode;
  getKPITrend: (kpi: KPIScore) => React.ReactNode;
  getRowBorderColor: (status: ExecutionStatus, kpi: KPIScore) => string;
  getRowBackgroundTint: (status: ExecutionStatus) => string;
  projectDate?: string | null;
  onSnackbar?: (payload: ExecutionSnackbarPayload) => void;
}

const PhaseRow = ({ phase, executionPlan, setExecutionPlan, onSave, onShowChangeOrder, expanded, onToggleExpand, expandedTasks, onToggleTask, getTaskNameById, visibleColumns, getStatusIcon, getKPITrend, getRowBorderColor, getRowBackgroundTint, projectDate = null, onSnackbar }: PhaseRowProps) => {
  const borderColor = getRowBorderColor(phase.status, phase.kpiScore);
  const bgTint = getRowBackgroundTint(phase.status);

  return (
    <>
      <TableRow 
        sx={{ 
          backgroundColor: '#e3f2fd',
          borderLeft: `4px solid ${borderColor}`,
          backgroundImage: bgTint !== 'transparent' ? `linear-gradient(to right, ${bgTint}, ${bgTint})` : 'none',
        }}
      >
        {visibleColumns.has('item') && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <IconButton size="small" onClick={onToggleExpand} sx={{ mt: -0.5 }}>
                {expanded ? <ExpandMore /> : <ChevronRight />}
              </IconButton>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {phase.name}
                </Typography>
                <ItemDescription text={phase.description} />
              </Box>
            </Box>
          </TableCell>
        )}
        {visibleColumns.has('status') && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getStatusIcon(phase.status)}
              <Chip 
                label={phase.status} 
                size="small" 
                color={getStatusColor(phase.status)}
              />
            </Box>
          </TableCell>
        )}
        {visibleColumns.has('percentComplete') && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={calculatePhasePercentComplete(phase)} 
                  sx={{ height: 14, borderRadius: 4 }}
                  color={calculatePhasePercentComplete(phase) === 100 ? 'success' : 'primary'}
                />
              </Box>
              <Typography variant="body2" sx={{ minWidth: 45, textAlign: 'right', fontWeight: 'bold' }}>
                {calculatePhasePercentComplete(phase)}%
              </Typography>
            </Box>
          </TableCell>
        )}
        {visibleColumns.has('assigned') && (
          <TableCell>
            {(() => {
              // Collect all unique assigned users from tasks in this phase
              const assignedUsers = new Set<string>();
              phase.tasks.forEach(task => {
                if (task.assignedUsers) {
                  task.assignedUsers.forEach(user => assignedUsers.add(user));
                }
              });
              const usersArray = Array.from(assignedUsers);
              
              if (usersArray.length === 0) {
                return <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>-</Typography>;
              }
              
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {usersArray.map(user => (
                    <Chip 
                      key={user}
                      label={user}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  ))}
                </Box>
              );
            })()}
          </TableCell>
        )}
        {visibleColumns.has('prerequisites') && (
          <TableCell>
            {phase.prerequisites && phase.prerequisites.length > 0 ? (
              <Typography variant="body2">
                {phase.prerequisites
                  .map(prereqId => executionPlan.phases.find(p => p.phaseId === prereqId)?.name ?? `Phase ${prereqId}`)
                  .join(', ')}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>(none)</Typography>
            )}
          </TableCell>
        )}
        {visibleColumns.has('documentName') && <TableCell>{'\u00A0'}</TableCell>}
        {visibleColumns.has('instructions') && <TableCell>{'\u00A0'}</TableCell>}
        {visibleColumns.has('sourceDocuments') && <TableCell>{'\u00A0'}</TableCell>}
        {visibleColumns.has('duration') && (
          <TableCell>
            {phase.contractedDuration > 0 ? (
              <Typography variant="body2">
                {phase.contractedDuration >= 8 
                  ? `${(phase.contractedDuration / 8).toFixed(1)} days` 
                  : `${phase.contractedDuration} hrs`}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>-</Typography>
            )}
          </TableCell>
        )}
        {visibleColumns.has('contractedStart') && (
          <TableCell>
            {phase.contractedStartDate ? (() => {
              const date = dateStringToLocalDate(phase.contractedStartDate);
              if (!date) {
                console.warn(`Phase ${phase.phaseId} contractedStartDate failed to parse:`, phase.contractedStartDate);
                return phase.contractedStartDate; // Fallback to raw string
              }
              return format(date, 'MMM dd, yyyy');
            })() : '-'}
          </TableCell>
        )}
        {visibleColumns.has('contractedEnd') && (
          <TableCell>
            {phase.contractedEndDate ? (() => {
              const date = dateStringToLocalDate(phase.contractedEndDate);
              if (!date) {
                console.warn(`Phase ${phase.phaseId} contractedEndDate failed to parse:`, phase.contractedEndDate);
                return phase.contractedEndDate; // Fallback to raw string
              }
              return format(date, 'MMM dd, yyyy');
            })() : '-'}
          </TableCell>
        )}
        {visibleColumns.has('actualStart') && (
          <TableCell>
            {formatActualStartForDisplay(phase.status, phase.actualStartDate)}
          </TableCell>
        )}
        {visibleColumns.has('actualEnd') && (
          <TableCell>
            {phase.actualEndDate ? (() => {
              const date = dateStringToLocalDate(phase.actualEndDate);
              return date ? format(date, 'MMM dd, yyyy') : '-';
            })() : '-'}
          </TableCell>
        )}
        {visibleColumns.has('kpiScore') && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getKPITrend(phase.kpiScore)}
              <Chip 
                label={phase.kpiScore === 0 ? 'On Time' : phase.kpiScore > 0 ? `+${phase.kpiScore}d` : `${phase.kpiScore}d`}
                size="small"
                color={getKPIColor(phase.kpiScore)}
              />
            </Box>
          </TableCell>
        )}
        {visibleColumns.has('actions') && (
          <TableCell>
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>—</Typography>
          </TableCell>
        )}
      </TableRow>
      {expanded && phase.tasks.map(task => (
        <TaskRow 
          key={task.taskId}
          task={task}
          phase={phase}
          executionPlan={executionPlan}
          setExecutionPlan={setExecutionPlan}
          onSave={onSave}
          onShowChangeOrder={onShowChangeOrder}
          expanded={expandedTasks.has(task.taskId.toString())}
          onToggleExpand={() => onToggleTask(task.taskId.toString())}
          getTaskNameById={getTaskNameById}
          visibleColumns={visibleColumns}
          getStatusIcon={getStatusIcon}
          getKPITrend={getKPITrend}
          getRowBorderColor={getRowBorderColor}
          getRowBackgroundTint={getRowBackgroundTint}
          projectDate={projectDate}
          onSnackbar={onSnackbar}
        />
      ))}
    </>
  );
};

// Task Row Component
interface TaskRowProps {
  task: ExecutionTask;
  phase: ExecutionPhase;
  executionPlan: ExecutionPlan;
  setExecutionPlan: (plan: ExecutionPlan) => void;
  onSave: (plan: ExecutionPlan) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onShowChangeOrder: (item: any) => void;
  expanded: boolean;
  onToggleExpand: () => void;
  getTaskNameById: (taskId: string) => string;
  visibleColumns: Set<ExecutionColumnKey>;
  getStatusIcon: (status: ExecutionStatus) => React.ReactNode;
  getKPITrend: (kpi: KPIScore) => React.ReactNode;
  getRowBorderColor: (status: ExecutionStatus, kpi: KPIScore) => string;
  getRowBackgroundTint: (status: ExecutionStatus) => string;
  projectDate?: string | null;
  onSnackbar?: (payload: ExecutionSnackbarPayload) => void;
}

const TaskRow = ({ task, phase, executionPlan, setExecutionPlan, onSave, onShowChangeOrder, expanded, onToggleExpand, getTaskNameById, visibleColumns, getStatusIcon, getKPITrend, getRowBorderColor, getRowBackgroundTint, projectDate = null, onSnackbar }: TaskRowProps) => {
  const borderColor = getRowBorderColor(task.status, task.kpiScore);
  const bgTint = getRowBackgroundTint(task.status);

  return (
    <>
      <TableRow 
        sx={{ 
          backgroundColor: '#f3e5f5',
          borderLeft: `4px solid ${borderColor}`,
          backgroundImage: bgTint !== 'transparent' ? `linear-gradient(to right, ${bgTint}, ${bgTint})` : 'none',
        }}
      >
        {visibleColumns.has('item') && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', pl: 4 }}>
              <IconButton size="small" onClick={onToggleExpand} sx={{ mt: -0.5 }}>
                {expanded ? <ExpandMore /> : <ChevronRight />}
              </IconButton>
              <Box>
                <Typography variant="body2">
                  {task.name}
                </Typography>
                <ItemDescription text={task.description} />
              </Box>
            </Box>
          </TableCell>
        )}
        {visibleColumns.has('status') && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getStatusIcon(task.status)}
              <Chip 
                label={task.status} 
                size="small" 
                color={getStatusColor(task.status)}
              />
            </Box>
          </TableCell>
        )}
        {visibleColumns.has('percentComplete') && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={calculateTaskPercentComplete(task)} 
                  sx={{ height: 14, borderRadius: 4 }}
                  color={calculateTaskPercentComplete(task) === 100 ? 'success' : 'primary'}
                />
              </Box>
              <Typography variant="body2" sx={{ minWidth: 45, textAlign: 'right', fontWeight: 'bold' }}>
                {calculateTaskPercentComplete(task)}%
              </Typography>
            </Box>
          </TableCell>
        )}
        {visibleColumns.has('assigned') && (
          <TableCell>
            {task.assignedUsers && task.assignedUsers.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {task.assignedUsers.map(user => (
                  <Chip 
                    key={user}
                    label={user}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>-</Typography>
            )}
          </TableCell>
        )}
        {visibleColumns.has('prerequisites') && (
          <TableCell>
            {task.prerequisites && task.prerequisites.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {task.prerequisites.map(prereqId => (
                  <Chip 
                    key={prereqId}
                    label={getTaskNameById(String(prereqId))}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>(none)</Typography>
            )}
          </TableCell>
        )}
        {visibleColumns.has('documentName') && <TableCell>{'\u00A0'}</TableCell>}
        {visibleColumns.has('instructions') && <TableCell>{'\u00A0'}</TableCell>}
        {visibleColumns.has('sourceDocuments') && <TableCell>{'\u00A0'}</TableCell>}
        {visibleColumns.has('duration') && (
          <TableCell>
            {task.contractedDuration > 0 ? (
              <Typography variant="body2">
                {task.contractedDuration >= 8 
                  ? `${(task.contractedDuration / 8).toFixed(1)} days` 
                  : `${task.contractedDuration} hrs`}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>-</Typography>
            )}
          </TableCell>
        )}
        {visibleColumns.has('contractedStart') && (
          <TableCell>
            {task.contractedStartDate ? (() => {
              const date = dateStringToLocalDate(task.contractedStartDate);
              if (!date) {
                console.warn(`Task ${task.taskId} contractedStartDate failed to parse:`, task.contractedStartDate);
                return task.contractedStartDate; // Fallback to raw string
              }
              return format(date, 'MMM dd, yyyy');
            })() : '-'}
          </TableCell>
        )}
        {visibleColumns.has('contractedEnd') && (
          <TableCell>
            {task.contractedEndDate ? (() => {
              const date = dateStringToLocalDate(task.contractedEndDate);
              if (!date) {
                console.warn(`Task ${task.taskId} contractedEndDate failed to parse:`, task.contractedEndDate);
                return task.contractedEndDate; // Fallback to raw string
              }
              return format(date, 'MMM dd, yyyy');
            })() : '-'}
          </TableCell>
        )}
        {visibleColumns.has('actualStart') && (
          <TableCell>
            {formatActualStartForDisplay(task.status, task.actualStartDate)}
          </TableCell>
        )}
        {visibleColumns.has('actualEnd') && (
          <TableCell>
            {task.actualEndDate ? (() => {
              const date = dateStringToLocalDate(task.actualEndDate);
              return date ? format(date, 'MMM dd, yyyy') : '-';
            })() : '-'}
          </TableCell>
        )}
        {visibleColumns.has('kpiScore') && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getKPITrend(task.kpiScore)}
              <Chip 
                label={task.kpiScore === 0 ? 'On Time' : task.kpiScore > 0 ? `+${task.kpiScore}d` : `${task.kpiScore}d`}
                size="small"
                color={getKPIColor(task.kpiScore)}
              />
            </Box>
          </TableCell>
        )}
        {visibleColumns.has('actions') && (
          <TableCell>
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>—</Typography>
          </TableCell>
        )}
      </TableRow>
      {expanded && task.actions.map(action => (
        <ActionRow 
          key={action.actionId}
          action={action}
          task={task}
          phase={phase}
          executionPlan={executionPlan}
          setExecutionPlan={setExecutionPlan}
          onSave={onSave}
          onShowChangeOrder={onShowChangeOrder}
          visibleColumns={visibleColumns}
          getStatusIcon={getStatusIcon}
          getKPITrend={getKPITrend}
          getRowBorderColor={getRowBorderColor}
          getRowBackgroundTint={getRowBackgroundTint}
          projectDate={projectDate}
          onSnackbar={onSnackbar}
        />
      ))}
    </>
  );
};

// Action Row Component
interface ActionRowProps {
  action: ExecutionAction;
  task: ExecutionTask;
  phase: ExecutionPhase;
  executionPlan: ExecutionPlan;
  setExecutionPlan: (plan: ExecutionPlan) => void;
  onSave: (plan: ExecutionPlan) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onShowChangeOrder: (item: any) => void;
  visibleColumns: Set<ExecutionColumnKey>;
  getStatusIcon: (status: ExecutionStatus) => React.ReactNode;
  getKPITrend: (kpi: KPIScore) => React.ReactNode;
  getRowBorderColor: (status: ExecutionStatus, kpi: KPIScore) => string;
  getRowBackgroundTint: (status: ExecutionStatus) => string;
  projectDate?: string | null;
  onSnackbar?: (payload: ExecutionSnackbarPayload) => void;
}

const ActionRow = ({ action, task, phase, executionPlan, setExecutionPlan: _setExecutionPlan, onSave: _onSave, onShowChangeOrder: _onShowChangeOrder, visibleColumns, getStatusIcon, getKPITrend, getRowBorderColor, getRowBackgroundTint, projectDate = null, onSnackbar }: ActionRowProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const borderColor = getRowBorderColor(action.status, action.kpiScore);
  const bgTint = getRowBackgroundTint(action.status);
  const [completionStatus, setCompletionStatus] = useState<CompletionStatus | null>(null);
  const [assignedUsers, setAssignedUsers] = useState<Array<{ userId: number; displayName: string | null; email: string }>>([]);
  const [_loadingCompletionStatus, setLoadingCompletionStatus] = useState(false);
  const [documents, setDocuments] = useState<DocumentAssociation[]>([]);
  const [_loadingDocuments, _setLoadingDocuments] = useState(false);
  const [resendEmailLoading, setResendEmailLoading] = useState(false);

  // Check if current user is assigned to this action
  const isUserAssigned = user && assignedUsers.length > 0 && 
    assignedUsers.some(assignedUser => assignedUser.email.toLowerCase() === user.email.toLowerCase());

  // Load completion status for CREATE/REVISE actions and assigned users for all actions
  useEffect(() => {
    const loadData = async () => {
      setLoadingCompletionStatus(true);
      try {
        // Load completion status (only for CREATE/REVISE)
        if (action.actionType === 'CREATE' || action.actionType === 'REVISE') {
          try {
            const status = await getActionCompletionStatus(action.actionId);
            setCompletionStatus(status);
          } catch (err) {
            // Ignore errors - completion status is optional
            console.debug('Could not load completion status for action', action.actionId, err);
          }
        }

        // Load assigned users for all action types
        try {
          const assignments = await getActionAssignments(action.actionId);
          setAssignedUsers(assignments.map(a => a.user));
        } catch (err) {
          // Ignore errors - assigned users are optional
          console.debug('Could not load assigned users for action', action.actionId, err);
        }

        // Load document associations only when we show pills (not CREATE, not NOT_STARTED)
        if (action.actionType !== 'CREATE' && action.status !== 'NOT_STARTED') {
          try {
            const docs = await apiService.getActionDocuments(action.actionId);
            setDocuments(docs);
          } catch (err) {
            if (action.actionType === 'REVISE' || action.actionType === 'REVIEW' || action.actionType === 'APPROVE') {
              console.error(`Failed to load documents for ${action.actionType} action ${action.actionId}:`, err);
            } else {
              console.debug('Could not load documents for action', action.actionId, err);
            }
            setDocuments([]);
          }
        } else {
          setDocuments([]);
        }
      } finally {
        setLoadingCompletionStatus(false);
      }
    };
    loadData();
  }, [action.actionId, action.actionType, action.status]);

  // Check if action is ready to trigger
  // Must satisfy: phase prerequisites complete, task prerequisites complete, then either
  // Condition A: Contracted start date has arrived, or
  // Condition B: Catch-up conditions (all previous actions in task complete)
  const isActionReadyToTrigger = (): boolean => {
    // If action is already triggered, in progress, or complete, it's not ready to trigger
    if (action.status === 'IN_PROGRESS' || action.status === 'COMPLETE' || action.status === 'LOCKED') {
      return false;
    }

    // Phase prerequisites: all prerequisite phases must be COMPLETE or LOCKED
    if (phase.prerequisites && phase.prerequisites.length > 0) {
      const prerequisitePhases = executionPlan.phases.filter(p =>
        phase.prerequisites!.includes(p.phaseId)
      );
      const allPhasePrereqsComplete = prerequisitePhases.every(prereqPhase =>
        prereqPhase.status === 'COMPLETE' || prereqPhase.status === 'LOCKED'
      );
      if (!allPhasePrereqsComplete) {
        return false; // Phase prerequisite(s) not met - do not show Ready to Trigger
      }
    }

    // Task prerequisites: all prerequisite tasks must be COMPLETE or LOCKED
    if (task.prerequisites && task.prerequisites.length > 0) {
      const prerequisiteTasks: ExecutionTask[] = [];
      executionPlan.phases.forEach(p => {
        p.tasks.forEach(t => {
          if (task.prerequisites!.includes(t.taskId)) {
            prerequisiteTasks.push(t);
          }
        });
      });
      const allTaskPrereqsComplete = prerequisiteTasks.every(prereqTask =>
        prereqTask.status === 'COMPLETE' || prereqTask.status === 'LOCKED'
      );
      if (!allTaskPrereqsComplete) {
        return false; // Task prerequisite(s) not met - do not show Ready to Trigger
      }
    }

    // Condition B / VALIDATE waterfall: prior display-order actions complete is not enough when
    // ActionPrerequisites exist — those must be complete too.
    if (action.actionType === 'VALIDATE' && action.prerequisites && action.prerequisites.length > 0) {
      const prereqActions: ExecutionAction[] = [];
      executionPlan.phases.forEach((p) => {
        p.tasks.forEach((t) => {
          t.actions.forEach((a) => {
            if (action.prerequisites!.includes(a.actionId)) {
              prereqActions.push(a);
            }
          });
        });
      });
      const allActionPrereqsComplete = prereqActions.every(
        (a) => a.status === 'COMPLETE' || a.status === 'LOCKED'
      );
      if (!allActionPrereqsComplete) {
        return false;
      }
      return true; // VALIDATE ActionPrerequisites met — ready even if contracted start is in the future
    }

    // Condition B: Check if all prerequisite actions in the task are complete (catch-up)
    const allPreviousActionsComplete = task.actions
      .filter(a => a.displayOrder < action.displayOrder)
      .every(a => a.status === 'COMPLETE' || a.status === 'LOCKED');

    if (allPreviousActionsComplete) {
      return true; // Catch-up condition met
    }

    // Condition A: Check if contracted start date has arrived
    // Dates are stored as ISO 8601 strings, need to convert for comparison
    if (action.contractedStartDate && projectDate) {
      try {
        const contractedDate = typeof action.contractedStartDate === 'string'
          ? new Date(action.contractedStartDate)
          : action.contractedStartDate;
        const currentDate = new Date(projectDate);
        // Compare dates (ignore time, as actions trigger at 8:00 AM)
        contractedDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);
        if (currentDate >= contractedDate) {
          return true; // Start date has arrived
        }
      } catch (e) {
        // Invalid date, skip this check
        console.warn('Invalid date format for action trigger check:', e);
      }
    }

    return false;
  };

  const actionReadyToTrigger = isActionReadyToTrigger();

  return (
    <TableRow
      sx={{
        borderLeft: `4px solid ${borderColor}`,
        backgroundImage: bgTint !== 'transparent' ? `linear-gradient(to right, ${bgTint}, ${bgTint})` : 'none',
      }}
    >
      {visibleColumns.has('item') && (
        <TableCell>
          <Box sx={{ pl: 8 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                {action.actionType}
              </Typography>
            {action.actionType === 'APPROVE' && action.rejectActionId != null && (() => {
              const rejectPlanId = getSourceActionPosition(executionPlan, action.rejectActionId);
              if (!rejectPlanId) return null;
              return (
                <Tooltip title={`On rejection, rolls back to plan action ${rejectPlanId}`}>
                  <Chip
                    label={`Reject → ${rejectPlanId}`}
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                </Tooltip>
              );
            })()}
            {/* Completion Status for CREATE/REVISE actions */}
            {(action.actionType === 'VALIDATE' || action.actionType === 'REVIEW') && (action.status === 'COMPLETE' || action.status === 'LOCKED') && (
              <Box sx={{ minWidth: 220 }}>
                <ActionComments actionId={action.actionId} title="Workflow comments" />
              </Box>
            )}
            {(action.actionType === 'CREATE' || action.actionType === 'REVISE') && completionStatus?.isCompleted && (
              <Tooltip 
                title={`Completed by ${completionStatus.completedBy?.displayName || completionStatus.completedBy?.email} on ${completionStatus.completedAt ? new Date(completionStatus.completedAt).toLocaleDateString() : 'unknown date'}`}
              >
                <Chip 
                  icon={<CheckCircle sx={{ fontSize: 14 }} />}
                  label={`Completed by ${completionStatus.completedBy?.displayName || completionStatus.completedBy?.email}`}
                  size="small" 
                  color="success"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              </Tooltip>
            )}
            {actionReadyToTrigger && action.status === 'NOT_STARTED' && (
              <Tooltip title="Ready to trigger — prerequisites met (fires on completion even if contracted start is later)">
                <Chip 
                  label="Ready to Trigger" 
                  size="small" 
                  color="success"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              </Tooltip>
            )}
            {action.status === 'IN_PROGRESS' && (
              <Tooltip title="Action has been triggered and is in progress">
                <Chip 
                  label="Triggered" 
                  size="small" 
                  color="info"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              </Tooltip>
            )}
            {action.isLocked && <Lock sx={{ ml: 1, fontSize: 16, color: '#999' }} />}
            </Box>
            {action.name?.trim() && (
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {action.name}
              </Typography>
            )}
            <ItemDescription text={action.description} />
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('status') && (
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getStatusIcon(action.status)}
            <Chip 
              label={action.status} 
              size="small" 
              color={getStatusColor(action.status)}
            />
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('percentComplete') && (
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={calculateActionPercentComplete(action)} 
                sx={{ height: 14, borderRadius: 4 }}
                color={calculateActionPercentComplete(action) === 100 ? 'success' : 'primary'}
              />
            </Box>
            <Typography variant="body2" sx={{ minWidth: 45, textAlign: 'right', fontWeight: 'bold' }}>
              {calculateActionPercentComplete(action)}%
            </Typography>
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('assigned') && (
        <TableCell>
          {/* Show assigned users from API if available, otherwise fall back to legacy assignedTo */}
          {assignedUsers.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {assignedUsers.map(user => (
                <Chip 
                  key={user.userId}
                  label={user.displayName || user.email}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                  title={user.email}
                />
              ))}
            </Box>
          ) : action.assignedTo && action.assignedTo.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {action.assignedTo.map(user => (
                <Chip 
                  key={user}
                  label={user}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>-</Typography>
          )}
        </TableCell>
      )}
      {visibleColumns.has('prerequisites') && (
        <TableCell>
          {action.actionType === 'VALIDATE' ? (
            action.prerequisites && action.prerequisites.length > 0 && executionPlan ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {action.prerequisites.map((prereqId) => {
                  const planId = getSourceActionPosition(executionPlan, prereqId);
                  return (
                    <Chip
                      key={prereqId}
                      label={planId || `Action #${prereqId}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                (none)
              </Typography>
            )
          ) : action.actionType === 'CREATE' || action.status === 'NOT_STARTED' ? (
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>-</Typography>
          ) : documents.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
              {/* COMPLETE/LOCKED: show only "View completed document" pill (download with correct filename; avoids blob: Save As UUID) */}
              {(action.status === 'COMPLETE' || action.status === 'LOCKED') && (action.actionType === 'REVISE' || action.actionType === 'REVIEW' || action.actionType === 'APPROVE') ? (
                (() => {
                  const doc = documents.find(d => !d.isPlaceholder && (d.spItemId || d.localFileId));
                  if (!doc) return <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>-</Typography>;
                  const openDoc = async () => {
                    try {
                      const preferredName =
                        doc.fileName || doc.documentTitle || 'document';
                      if (doc.storageType === 'SHAREPOINT' && (doc.webUrl || doc.spItemId)) {
                        if (shouldOpenSharePointDirectly(user) && doc.webUrl) {
                          window.open(doc.webUrl, '_blank');
                          return;
                        }
                        try {
                          const { blob, fileName } = await apiService.viewFileWithInfo(doc.spItemId!, 'SHAREPOINT');
                          apiService.triggerBlobDownload(blob, fileName || preferredName);
                        } catch {
                          const { blob, fileName } = await apiService.downloadFileWithInfo(doc.spItemId!, 'SHAREPOINT');
                          apiService.triggerBlobDownload(blob, fileName || preferredName);
                        }
                      } else if (doc.storageType === 'LOCAL' && doc.localFileId) {
                        const { blob, fileName } = await apiService.downloadFileWithInfo(doc.localFileId, 'LOCAL');
                        apiService.triggerBlobDownload(blob, fileName || preferredName);
                      }
                    } catch (err) {
                      console.error('Failed to open document:', err);
                    }
                  };
                  return (
                    <Tooltip title="Download the current document version (uses server file name and extension)">
                      <Chip
                        icon={<Description />}
                        label="View completed document"
                        size="small"
                        color="error"
                        variant="filled"
                        sx={{ fontSize: '0.7rem', height: 20, cursor: 'pointer' }}
                        onClick={openDoc}
                      />
                    </Tooltip>
                  );
                })()
              ) : (
                /* IN_PROGRESS: show only source (prerequisite) document pills; both open current version */
                (() => {
                  const uniqueDocsMap = new Map();
                  documents.forEach(d => {
                    const key = d.documentNumber || d.actionDocumentId || `doc-${d.fileName || 'unknown'}-${documents.indexOf(d)}`;
                    if (!uniqueDocsMap.has(key) || (d.documentNumber && !uniqueDocsMap.get(key).documentNumber)) {
                      uniqueDocsMap.set(key, d);
                    }
                  });
                  const uniqueDocs = Array.from(uniqueDocsMap.values());
                  if (documents.length > 0 && uniqueDocs.length === 0) {
                    console.warn(`Action ${action.actionId} (${action.actionType}) - Documents loaded but none will display:`, {
                      totalDocuments: documents.length,
                      documentsWithoutNumber: documents.filter(d => !d.documentNumber).length,
                      allDocuments: documents.map(d => ({ actionDocumentId: d.actionDocumentId, documentNumber: d.documentNumber, fileName: d.fileName })),
                    });
                  }
                  return uniqueDocs.map((doc, index) => {
                    const sourcePosition = doc.sourceActionId && executionPlan
                      ? getSourceActionPosition(executionPlan, doc.sourceActionId!)
                      : null;
                    const docTitle = doc.documentTitle || doc.fileName || 'Document';
                    const chipLabel = `${docTitle} – ${action.actionType}${doc.isPlaceholder ? ' (Pending)' : ''}`;
                    const openDoc = async () => {
                      if (!doc.isPlaceholder && doc.storageType) {
                        try {
                          const preferredName =
                            doc.fileName || doc.documentTitle || 'document';
                          if (doc.storageType === 'SHAREPOINT' && (doc.webUrl || doc.spItemId)) {
                            if (shouldOpenSharePointDirectly(user) && doc.webUrl) {
                              window.open(doc.webUrl, '_blank');
                              return;
                            }
                            try {
                              const { blob, fileName } = await apiService.viewFileWithInfo(doc.spItemId!, 'SHAREPOINT');
                              apiService.triggerBlobDownload(blob, fileName || preferredName);
                            } catch {
                              const { blob, fileName } = await apiService.downloadFileWithInfo(doc.spItemId!, 'SHAREPOINT');
                              apiService.triggerBlobDownload(blob, fileName || preferredName);
                            }
                          } else if (doc.storageType === 'LOCAL' && doc.localFileId) {
                            const { blob, fileName } = await apiService.downloadFileWithInfo(doc.localFileId, 'LOCAL');
                            apiService.triggerBlobDownload(blob, fileName || preferredName);
                          }
                        } catch (err) {
                          console.error('Failed to open document:', err);
                        }
                      }
                    };
                    return (
                      <Tooltip
                        key={doc.actionDocumentId || doc.documentNumber || `doc-${index}`}
                        title={
                          <Box>
                            <Typography variant="caption" display="block">
                              {sourcePosition ? `Source: ${sourcePosition}` : (doc.fileName || `Document ${doc.documentNumber || doc.actionDocumentId}`)}
                            </Typography>
                            {(doc.documentTitle || doc.fileName) && (
                              <Typography variant="caption" display="block">{doc.documentTitle || doc.fileName}</Typography>
                            )}
                            {doc.storageType && <Typography variant="caption" display="block">Storage: {doc.storageType}</Typography>}
                            {doc.isPlaceholder && (
                              <Typography variant="caption" display="block" color="warning.main">Placeholder - Not uploaded yet</Typography>
                            )}
                          </Box>
                        }
                      >
                        <Chip
                          icon={doc.isPlaceholder ? <RadioButtonUnchecked /> : <Description />}
                          label={chipLabel}
                          size="small"
                          color={doc.isPlaceholder ? 'default' : 'error'}
                          variant={doc.isPlaceholder ? 'outlined' : 'filled'}
                          sx={{ fontSize: '0.7rem', height: 20, cursor: doc.isPlaceholder ? 'default' : 'pointer' }}
                          onClick={openDoc}
                        />
                      </Tooltip>
                    );
                  });
                })()
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>-</Typography>
          )}
        </TableCell>
      )}
      {visibleColumns.has('documentName') && (
        <TableCell>
          {(action.actionType === 'CREATE' || action.actionType === 'REVISE') && action.documentName
            ? action.documentName
            : '\u00A0'}
        </TableCell>
      )}
      {visibleColumns.has('instructions') && (
        <TableCell>
          {(action.actionType === 'CREATE' || action.actionType === 'REVISE') && action.instructions
            ? action.instructions
            : action.actionType === 'VALIDATE' && (action.description || action.instructions)
              ? (action.description || action.instructions)
            : '\u00A0'}
        </TableCell>
      )}
      {visibleColumns.has('sourceDocuments') && (
        <TableCell>
          {!action.sourceDocuments || action.sourceDocuments.length === 0 ? (
            '—'
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {action.sourceDocuments.map((doc, idx) => (
                <span key={doc.sourceActionId ?? idx}>
                  {doc.isReady && doc.currentVersionUrl ? (
                    <a
                      href={doc.currentVersionUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.875rem' }}
                    >
                      {doc.documentTitle || 'Source document'}
                    </a>
                  ) : (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Document not yet ready
                    </Typography>
                  )}
                </span>
              ))}
            </Box>
          )}
        </TableCell>
      )}
      {visibleColumns.has('duration') && (
        <TableCell>
          {action.duration > 0 ? (
            <Typography variant="body2">
              {action.duration >= 8 
                ? `${(action.duration / 8).toFixed(1)} days` 
                : `${action.duration} hrs`}
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>-</Typography>
          )}
        </TableCell>
      )}
      {visibleColumns.has('contractedStart') && (
        <TableCell>
          {action.contractedStartDate ? (() => {
            const date = dateStringToLocalDate(action.contractedStartDate);
            if (!date) {
              console.warn(`Action ${action.actionId} contractedStartDate failed to parse:`, action.contractedStartDate);
              return action.contractedStartDate; // Fallback to raw string
            }
            return format(date, 'MMM dd, yyyy');
          })() : '-'}
        </TableCell>
      )}
      {visibleColumns.has('contractedEnd') && (
        <TableCell>
          {action.contractedEndDate ? (() => {
            const date = dateStringToLocalDate(action.contractedEndDate);
            if (!date) {
              console.warn(`Action ${action.actionId} contractedEndDate failed to parse:`, action.contractedEndDate);
              return action.contractedEndDate; // Fallback to raw string
            }
            return format(date, 'MMM dd, yyyy');
          })() : '-'}
        </TableCell>
      )}
      {visibleColumns.has('actualStart') && (
        <TableCell>
          {formatActualStartForDisplay(action.status, action.actualStartDate)}
        </TableCell>
      )}
      {visibleColumns.has('actualEnd') && (
        <TableCell>
          {action.actualEndDate ? (() => {
            const date = dateStringToLocalDate(action.actualEndDate);
            return date ? format(date, 'MMM dd, yyyy') : '-';
          })() : '-'}
        </TableCell>
      )}
      {visibleColumns.has('kpiScore') && (
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getKPITrend(action.kpiScore)}
            <Chip 
              label={action.kpiScore === 0 ? 'On Time' : action.kpiScore > 0 ? `+${action.kpiScore}d` : `${action.kpiScore}d`}
              size="small"
              color={getKPIColor(action.kpiScore)}
              title={action.kpiScore !== 0 ? `KPI Score: ${action.kpiScore > 0 ? '+' : ''}${action.kpiScore} business days` : 'On Time'}
            />
          </Box>
        </TableCell>
      )}
      {visibleColumns.has('actions') && (
        <TableCell>
          {action.status === 'IN_PROGRESS' && (isUserAssigned || (user && hasRole(user, 'System Admin'))) ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {isUserAssigned && (
                <Tooltip title="Open action form">
                  <Link
                    to={`/create-action?projectId=${executionPlan.projectId}&actionId=${action.actionId}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Chip
                      label="Open Action"
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 24, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/create-action?projectId=${executionPlan.projectId}&actionId=${action.actionId}`);
                      }}
                    />
                  </Link>
                </Tooltip>
              )}
              {user && hasRole(user, 'System Admin') && (
                <Tooltip title="Resend the assignment email to all users assigned to this action">
                  <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    disabled={resendEmailLoading}
                    sx={{ fontSize: '0.7rem', minWidth: 'auto', py: 0.25, px: 1 }}
                    onClick={async () => {
                      setResendEmailLoading(true);
                      try {
                        const token = getUserAccessToken();
                        const result = await apiService.resendActionAssignmentEmail(action.actionId, token);
                        onSnackbar?.({
                          open: true,
                          message: `Action email resent (${result.recipientsNotified} recipient(s)).`,
                          severity: 'success',
                        });
                      } catch (err: unknown) {
                        const ax = err as { response?: { data?: { message?: string } }; message?: string };
                        const msg =
                          ax.response?.data?.message ||
                          ax.message ||
                          'Failed to resend action email';
                        onSnackbar?.({
                          open: true,
                          message: msg,
                          severity: 'error',
                        });
                      } finally {
                        setResendEmailLoading(false);
                      }
                    }}
                  >
                    Resend email
                  </Button>
                </Tooltip>
              )}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>—</Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

// Change Orders View Component
interface ChangeOrdersViewProps {
  projectId: number;
  changeOrders: ChangeOrderListItem[];
  /** When false, download/upload/validate/commit are hidden (Client/Tenant users) */
  canManipulateChangeOrders?: boolean;
  onRefresh: () => Promise<void>;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onSnackbar: (s: { open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }) => void;
}

const ChangeOrdersView = ({ projectId, changeOrders, canManipulateChangeOrders = true, onRefresh, onApprove: _onApprove, onReject: _onReject, onSnackbar }: ChangeOrdersViewProps) => {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateResult, setValidateResult] = useState<{ valid: boolean; impactPreview?: ChangeOrderValidateResponse['impactPreview']; errors?: Array<{ row: number; column: string; message: string; code?: string }> } | null>(null);
  const [justificationOpen, setJustificationOpen] = useState(false);
  const [commitTitle, setCommitTitle] = useState('');
  const [commitSummary, setCommitSummary] = useState('');
  const [commitNotifyUsers, setCommitNotifyUsers] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      await downloadChangeOrderExport(projectId);
      onSnackbar({ open: true, message: 'Change Order spreadsheet downloaded.', severity: 'success' });
    } catch (err: unknown) {
      const e = err as { message?: string };
      onSnackbar({ open: true, message: e.message || 'Download failed', severity: 'error' });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!selectedFile) {
      onSnackbar({ open: true, message: 'Select a file first', severity: 'warning' });
      return;
    }
    setValidateLoading(true);
    setValidateResult(null);
    try {
      const result = await validateChangeOrder(projectId, selectedFile);
      setValidateResult({
        valid: result.valid,
        impactPreview: result.impactPreview,
        errors: (result as { errors?: Array<{ row: number; column: string; message: string; code?: string }> }).errors,
      });
      if (result.valid) {
        setJustificationOpen(true);
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      onSnackbar({ open: true, message: e.message || 'Validation failed', severity: 'error' });
    } finally {
      setValidateLoading(false);
    }
  };

  const handleCommit = async () => {
    if (!selectedFile) return;
    setCommitLoading(true);
    try {
      await commitChangeOrder(projectId, selectedFile, {
        title: commitTitle,
        summary: commitSummary,
        notifyUsers: commitNotifyUsers,
      });
      onSnackbar({ open: true, message: 'Change Order committed successfully.', severity: 'success' });
      setJustificationOpen(false);
      setSelectedFile(null);
      setValidateResult(null);
      setCommitTitle('');
      setCommitSummary('');
      setCommitNotifyUsers(false);
      await onRefresh();
    } catch (err: unknown) {
      const e = err as { message?: string };
      onSnackbar({ open: true, message: e.message || 'Commit failed', severity: 'error' });
    } finally {
      setCommitLoading(false);
    }
  };

  const impactPreview = validateResult?.impactPreview;

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          Change Order workflow
        </Typography>
        {canManipulateChangeOrders ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleDownload}
            disabled={downloadLoading}
          >
            {downloadLoading ? 'Downloading…' : 'Download Change Order Spreadsheet'}
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button variant="outlined" component="label" startIcon={<Upload />}>
              Choose file
              <input
                type="file"
                hidden
                accept=".xlsx"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setSelectedFile(f || null);
                  setValidateResult(null);
                }}
              />
            </Button>
            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                {selectedFile.name}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={handleValidate}
            disabled={!selectedFile || validateLoading}
          >
            {validateLoading ? 'Validating…' : 'Validate'}
          </Button>
        </Box>
        ) : (
          <Typography color="text.secondary">
            Download and upload of change orders are available to Fraport users only.
          </Typography>
        )}

        {validateResult && !validateResult.valid && validateResult.errors && validateResult.errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Validation errors</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {validateResult.errors.slice(0, 10).map((err, i) => (
                <li key={i}>{err.message}</li>
              ))}
              {validateResult.errors.length > 10 && <li>…and {validateResult.errors.length - 10} more</li>}
            </ul>
          </Alert>
        )}

        {validateResult?.valid && impactPreview && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Impact preview</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {impactPreview.recordsAdded?.length > 0 && (
                <li>Records to add: {impactPreview.recordsAdded.length}</li>
              )}
              {impactPreview.recordsEdited?.length > 0 && (
                <li>Records to edit: {impactPreview.recordsEdited.length}</li>
              )}
              {impactPreview.recordsDeleted?.length > 0 && (
                <li>Records to delete: {impactPreview.recordsDeleted.length}</li>
              )}
              {impactPreview.prerequisitesModified && <li>Prerequisites modified</li>}
              {impactPreview.assignmentChanges && <li>Assignment changes</li>}
            </Box>
          </Box>
        )}
      </Paper>

      <Dialog open={justificationOpen} onClose={() => !commitLoading && setJustificationOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Commit Change Order</DialogTitle>
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            margin="dense"
            value={commitTitle}
            onChange={(e) => setCommitTitle(e.target.value)}
          />
          <TextField
            label="Justification / Summary"
            fullWidth
            multiline
            rows={3}
            margin="dense"
            value={commitSummary}
            onChange={(e) => setCommitSummary(e.target.value)}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Notify users?</InputLabel>
            <Select
              value={commitNotifyUsers ? 'yes' : 'no'}
              label="Notify users?"
              onChange={(e) => setCommitNotifyUsers(e.target.value === 'yes')}
            >
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJustificationOpen(false)} disabled={commitLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleCommit} disabled={commitLoading}>
            {commitLoading ? 'Committing…' : 'Commit'}
          </Button>
        </DialogActions>
      </Dialog>

      {changeOrders.length === 0 ? (
        <Alert severity="info">
          No change orders have been created yet. Use Download → edit → Validate → Commit to submit a change order.
        </Alert>
      ) : (
        <>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1 }}>
            Completed change orders
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Change Order #</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Submitted by</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changeOrders.map(co => (
                  <TableRow key={co.changeOrderId}>
                    <TableCell>{co.changeOrderNumber}</TableCell>
                    <TableCell>{co.title || co.changeOrderNumber}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }} title={co.summary || ''}>
                      {co.summary ? (co.summary.length > 60 ? `${co.summary.slice(0, 60)}…` : co.summary) : '—'}
                    </TableCell>
                    <TableCell>
                      {co.submittedOn ? format(new Date(co.submittedOn), 'MMM dd, yyyy HH:mm') : (co.requestedDate || '—')}
                    </TableCell>
                    <TableCell>{co.submittedByDisplayName || co.submittedByEmail || '—'}</TableCell>
                    <TableCell>{co.fromVersion} → {co.toVersion}</TableCell>
                    <TableCell>
                      <Chip 
                        label={co.status} 
                        size="small"
                        color={co.status === 'APPROVED' ? 'success' : co.status === 'REJECTED' ? 'error' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

// KPI Dashboard Component
interface KPIDashboardProps {
  executionPlan: ExecutionPlan;
}

const KPIDashboard = ({ executionPlan }: KPIDashboardProps) => {
  // EKPI-F2: Display only backend KPI scores by item. Do not compute attribution or responsibility in the frontend.
  // For "who caused delays" / actor attribution, use the Audit Log tab (backend-driven).
  const allKPIs = useMemo(() => {
    const kpis: { item: string; kpi: KPIScore; type: string }[] = [];
    executionPlan.phases.forEach(phase => {
      kpis.push({ item: phase.name, kpi: phase.kpiScore, type: 'Phase' });
      phase.tasks.forEach(task => {
        kpis.push({ item: task.name, kpi: task.kpiScore, type: 'Task' });
        task.actions.forEach(action => {
          kpis.push({ item: action.name ?? `${action.actionType} - ${action.notes}`, kpi: action.kpiScore, type: 'Action' });
        });
      });
    });
    return kpis;
  }, [executionPlan]);

  const aheadOfSchedule = allKPIs.filter(k => k.kpi < 0).length;
  const onTime = allKPIs.filter(k => k.kpi === 0).length;
  const late = allKPIs.filter(k => k.kpi > 0).length;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        KPI scores by item (from backend). For actor attribution and delay causation, use the <strong>Audit Log</strong> tab.
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="success.main">{aheadOfSchedule}</Typography>
          <Typography variant="body2">Ahead of Schedule</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="default">{onTime}</Typography>
          <Typography variant="body2">On Time</Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
          <Typography variant="h4" color="error.main">{late}</Typography>
          <Typography variant="body2">Late</Typography>
        </Paper>
      </Box>

      {/* Data Table - By Item only (backend values) */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>KPI Score</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allKPIs.map((kpi, index) => (
              <TableRow key={index}>
                <TableCell>{kpi.item}</TableCell>
                <TableCell>{kpi.type}</TableCell>
                <TableCell>
                  <Chip
                    label={kpi.kpi === 0 ? 'On Time' : kpi.kpi > 0 ? `+${kpi.kpi}d` : `${kpi.kpi}d`}
                    size="small"
                    color={getKPIColor(kpi.kpi)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProjectExecutionPage;

